import { useState, useEffect } from 'react';
import {
  orgFileExists,
  orgCollectionHasEntries,
  listOrgCollection,
  readOrgFile,
  writeOrgFile,
} from '../storage/FileSystemAdapter';
import { computeHealthScore, computeAlerts, computeComplianceScore, computeRiskGaps, computeDocumentGaps, computeOrphanRefs } from '../utils/intelligence';
import { ALERT_CATALOG } from '../utils/alertCatalog';
import { MS_PER_DAY, HEALTH_COMPONENT_WEIGHTS } from '../utils/constants';
import { BG_SUBTLE, BG_CONTENT, BG_SURFACE, BG_HOVER_LT, BG_TABLE, TEXT_MUTED, TEXT_DISABLED, BORDER_DEFAULT, TEXT_HEADING, TEXT_DEFAULT, BRAND_PRIMARY, ERROR_BG, ERROR_TEXT, ERROR_BORDER, STATUS_NEUTRAL, STATUS_WARN, FIELD_ERROR_BORDER, FONT_LABEL, FONT_SMALL, FONT_TINY, FONT_BUTTON, FONT_LIST_ITEM, FONT_FAMILY_DISPLAY } from '../theme';
import { CONTENT_MAX_WIDTH } from '../App';
import { COUNTRIES } from '../utils/countries';
import { SECTORS } from '../utils/sectors';

// ─── MODULE DEFINITIONS ───────────────────────────────────────────────────────
const MODULES = [
  { id: 'context',           clauseRef: '4.1–4.2',      file: 'context.json',       isCollection: false, label: { es: 'Contexto de la organización',       en: 'Organizational context' } },
  { id: 'scope',             clauseRef: '4.3',           file: 'scope.json',         isCollection: false, label: { es: 'Alcance del SGSI',                  en: 'ISMS scope' } },
  { id: 'policy',            clauseRef: '5.2',           file: 'documents',          isCollection: true,  label: { es: 'Política de seguridad',    en: 'Information security policy' } },
  { id: 'risks',             clauseRef: '6.1.2',         file: 'risks',              isCollection: true,  label: { es: 'Gestión de riesgos',               en: 'Risk management' } },
  { id: 'soa',               clauseRef: '6.1.3',         file: 'controls/soa.json',  isCollection: false, label: { es: 'Declaración de aplicabilidad',      en: 'Statement of Applicability' } },
  { id: 'rtp',               clauseRef: '6.1.3',         file: 'rtp.json',           isCollection: false, label: { es: 'Plan de tratamiento de riesgos',    en: 'Risk treatment plan' } },
  { id: 'objectives',        clauseRef: '6.2',           file: 'objectives',         isCollection: true,  label: { es: 'Objetivos de seguridad',            en: 'Security objectives' } },
  { id: 'assets',            clauseRef: 'A.5.9',         file: 'assets',             isCollection: true,  label: { es: 'Inventario de activos',             en: 'Asset inventory' } },
  { id: 'suppliers',         clauseRef: 'A.5.19–5.22',   file: 'suppliers',          isCollection: true,  label: { es: 'Gestión de proveedores',            en: 'Supplier management' } },
  { id: 'customers',         clauseRef: '4.2',            file: 'customers',          isCollection: true,  label: { es: 'Gestión de clientes',               en: 'Customer management' } },
  { id: 'incidents',         clauseRef: 'A.5.24–5.27',   file: 'incidents',          isCollection: true,  label: { es: 'Gestión de incidentes',             en: 'Incident management' } },
  { id: 'documents',         clauseRef: '7.5',           file: 'documents',          isCollection: true,  label: { es: 'Información documentada',           en: 'Documented information' } },
  { id: 'competence',        clauseRef: '7.2',           file: 'competence',         isCollection: true,  label: { es: 'Competencia',                       en: 'Competence' } },
  { id: 'awareness',         clauseRef: '7.3',           file: 'awareness',          isCollection: true,  label: { es: 'Concienciación',                    en: 'Awareness' } },
  { id: 'metrics',           clauseRef: '9.1',           file: 'metrics',            isCollection: true,  label: { es: 'Métricas y KPIs', en: 'Metrics and KPIs' } },
  { id: 'audits',            clauseRef: '9.2',           file: 'audits',             isCollection: true,  label: { es: 'Auditoría interna',                 en: 'Internal audit' } },
  { id: 'management-reviews',clauseRef: '9.3',           file: 'management-reviews', isCollection: true,  label: { es: 'Revisión por la dirección',         en: 'Management review' } },
  { id: 'nonconformities',   clauseRef: '10.2',          file: 'nonconformities',    isCollection: true,  label: { es: 'No conformidades',                  en: 'Nonconformities' } },
  { id: 'legal-register',   clauseRef: '4.1–4.2',       file: 'legal-register',     isCollection: true,  label: { es: 'Registro legal',                    en: 'Legal register' } },
];

const CLAUSE_GROUPS = [
  { key: 'clause4',   label: { es: 'Cláusula 4 · Contexto',      en: 'Clause 4 · Context'      }, moduleIds: ['context', 'scope'] },
  { key: 'clause5',   label: { es: 'Cláusula 5 · Liderazgo',     en: 'Clause 5 · Leadership'   }, moduleIds: ['policy'] },
  { key: 'clause6',   label: { es: 'Cláusula 6 · Planificación', en: 'Clause 6 · Planning'     }, moduleIds: ['risks', 'soa', 'rtp', 'objectives'] },
  { key: 'clause7',   label: { es: 'Cláusula 7 · Soporte',       en: 'Clause 7 · Support'      }, moduleIds: ['competence', 'awareness', 'documents'] },
  { key: 'clause8',   label: { es: 'Cláusula 8 · Operación',     en: 'Clause 8 · Operation'    }, moduleIds: ['assets', 'suppliers', 'incidents'] },
  { key: 'clause9',   label: { es: 'Cláusula 9 · Evaluación',    en: 'Clause 9 · Evaluation'   }, moduleIds: ['metrics', 'audits', 'management-reviews'] },
  { key: 'clause10',  label: { es: 'Cláusula 10 · Mejora',       en: 'Clause 10 · Improvement' }, moduleIds: ['nonconformities'] },
  { key: 'registers', label: { es: 'Registros',                   en: 'Registers'               }, moduleIds: ['customers', 'legal-register'] },
];

// ─── INTELLIGENCE CONSTANTS ───────────────────────────────────────────────────
const SEVERITY_ORDER = ['critical', 'high', 'medium', 'low'];

const DEFAULT_DASH_CONFIG = {
  healthScore: true,
  riskPosture: true,
  nonconformities: true,
  alerts: true,
  riskGaps: true,
  documentGaps: true,
  orphanRefs: true,
  deadlines: true,
  controlGap: true,
  annexA: true,
  complianceScore: true,
  ismsProgress: true,
};

const SEV_COLORS = {
  critical: { dot: FIELD_ERROR_BORDER, bg: ERROR_BG, border: ERROR_BORDER, text: ERROR_TEXT }, // TODO: P6b — verify color role
  high:     { dot: '#d97706', bg: '#fffbeb', border: '#fcd34d', text: STATUS_WARN.text },
  medium:   { dot: TEXT_MUTED, bg: BG_TABLE, border: BORDER_DEFAULT, text: TEXT_DEFAULT },
  low:      { dot: TEXT_DISABLED, bg: BG_SUBTLE,  border: STATUS_NEUTRAL.bg, text: TEXT_MUTED },
};

// ─── I18N ─────────────────────────────────────────────────────────────────────
const T = {
  es: {
    executiveDashboard: 'Dashboard ejecutivo',
    sector: 'Sector', country: 'País',
    switchOrg: 'Cambiar organización', settings: 'Configuración',
    statusEmpty: 'Vacío', statusInProgress: 'En progreso', statusComplete: 'Completo',
    modulesTitle: 'Módulos del SGSI', comingSoon: 'Próximamente',
    certStatusLabel: 'Certificación',
    certStatusLabels: {
      none: 'Sin certificación', preparing: 'Preparando certificación',
      stage1: 'Fase 1', stage2: 'Fase 2', certified: 'Certificado',
      surveillance: 'Seguimiento', expired: 'Vencido',
    },
    // PDCA
    pdcaLabel: 'Ciclo PDCA',
    pdcaStages: { Plan: 'Planificar', Do: 'Hacer', Check: 'Verificar', Act: 'Actuar' },
    nextMilestone: 'Próximo hito',
    daysRemaining: 'Días restantes',
    // Health score
    healthScoreTitle: 'Salud del SGSI',
    hardGateNote: 'Puntuación limitada a 60 — requisitos clave pendientes',
    viewBreakdown: 'Ver desglose',
    hideBreakdown: 'Ocultar desglose',
    loading: 'Calculando…',
    componentLabels: {
      riskTreatment: 'Tratamiento de riesgos', controlImplementation: 'Implementación de controles',
      objectiveAchievement: 'Logro de objetivos', auditCadence: 'Cadencia de auditoría',
      mrCadence: 'Revisiones por dirección', ncClosure: 'Cierre de NCs',
      documentCurrency: 'Vigencia documental', awarenessCoverage: 'Concienciación',
      competenceCompletion: 'Competencia', supplierReviewCadence: 'Revisión proveedores',
      assetInventoryHealth: 'Inventario de activos',
    },
    // Alerts
    alertsTitle: 'Alertas de inteligencia',
    alertsNone: 'Sin alertas activas',
    showSnoozed: 'Mostrar pospuestas', hideSnoozed: 'Ocultar pospuestas',
    snoozedUntil: 'hasta',
    snooze: 'Posponer', snooze7: '7 días', snooze30: '30 días', snooze90: '90 días', reactivate: 'Reactivar', snoozeClose: 'Cerrar',
    snoozedCasesLabel: 'Pospuestos',
    severityLabels: { critical: 'Crítico', high: 'Alto', medium: 'Medio', low: 'Bajo' },
    alertTitles: {
      highRisksUntreated: 'Riesgos altos sin tratamiento',
      gdprNotificationOverdue: 'Notificación RGPD vencida (>72h)',
      nis2EarlyWarningOverdue: 'Alerta temprana NIS2 vencida (>24h)',
      nis2NotificationOverdue: 'Notificación NIS2 vencida (>72h)',
      nis2FinalReportOverdue: 'Informe final NIS2 vencido (>30d)',
      majorNcFindingWithoutRecord: 'Hallazgo de auditoría sin NC registrada',
      repeatFinding: 'No conformidad recurrente abierta',
      certificationExpired: 'Certificación vencida',
      certificationExpiring180: 'Certificación vence en <180 días',
      certificationExpiring90: 'Certificación vence en <90 días',
      residualRiskNotAccepted: 'Riesgo residual no aceptado tras tratamiento',
      controlGapOnHighRisk: 'Control no implementado vinculado a riesgo alto',
      overdueRtpActions: 'Acciones del plan de tratamiento vencidas',
      treatmentDeadlineMissing: 'Tratamiento sin fecha límite',
      openNcsOverdue: 'NC con acción correctiva vencida',
      ncClosedWithoutVerifier: 'NC cerrada sin verificador',
      policyNotApproved: 'Política de seguridad sin aprobar',
      policyApprovedWithoutEvidence: 'Política aprobada sin evidencia',
      policyOverdueForReview: 'Política vencida para revisión',
      soaNotApproved: 'Declaración de aplicabilidad sin aprobar',
      rtpNotApproved: 'Plan de tratamiento sin aprobar',
      scopeNotApproved: 'Alcance sin aprobar',
      soaApprovedWithoutEvidence: 'DdA aprobada sin evidencia',
      rtpApprovedWithoutEvidence: 'RTP aprobado sin evidencia',
      scopeApprovedWithoutEvidence: 'Alcance aprobado sin evidencia',
      topManagementCommitmentMissing: 'Sin evidencia de compromiso de la dirección',
      noInternalAuditWithinCycle: 'Sin auditoría interna en el ciclo',
      noManagementReviewWithinCycle: 'Sin revisión por dirección en el ciclo',
      mrWithoutTopManagement: 'Revisión sin presencia de la alta dirección',
      mrWithEmptyRequiredInput: 'Revisión con entradas obligatorias vacías',
      mrWithoutDecisions: 'Revisión sin decisiones registradas',
      mrWithDecisionsButNoActions: 'Decisiones sin acciones de mejora',
      supplierGdprGap: 'Proveedor con datos personales sin DPA',
      customerGdprGap: 'Cliente con datos personales sin DPA',
      incidentsWithoutNc: 'Incidente crítico/alto sin NC vinculada',
      riskAcceptedWithoutApprover: 'Riesgo aceptado sin aprobador',
      personInactiveInOwnerRoles: 'Persona inactiva como responsable',
      contextReviewOverdue: 'Revisión de contexto vencida (>12 meses)',
      documentsOverdueForReview: 'Documentos vencidos para revisión',
      documentApprovedWithoutApproverOrDate: 'Documento aprobado sin aprobador o fecha de aprobación',
      objectivesAtRisk: 'Objetivos en riesgo o no alcanzados',
      objectivesDeadlineOverdue: 'Objetivos con fecha límite vencida',
      objectivesWithoutMeasurement: 'Objetivos sin método de medición',
      supplierReviewsOverdue: 'Revisiones de proveedores vencidas',
      supplierCriticalWithoutExitPlan: 'Proveedor crítico sin plan de salida',
      supplierCriticalWithExpiredContract: 'Proveedor crítico con contrato vencido',
      incidentRootCauseMissing: 'Incidente sin análisis de causa raíz (>30d)',
      ncWithoutRootCauses: 'NC sin causas raíz identificadas',
      climateChangeNotAssessed: 'Impacto del cambio climático no evaluado',
      riskHighWithoutOwner: 'Riesgo alto sin propietario',
      riskReviewOverdue: 'Revisión de riesgo vencida',
      legalRegisterReviewOverdue: 'Revisión de obligación legal vencida',
      awarenessCompetenceNoProgramme: 'Sin programa de concienciación/competencia este año',
      awarenessCoverageBelowTarget: 'Cobertura de concienciación bajo objetivo',
      certificationStatusStale: 'Estado de certificación desactualizado',
      evidenceRetentionExpiring: 'Retención de evidencias vence en <90 días',
      stage1ImminentWithGaps: 'Auditoría fase 1 inminente con brechas documentales',
      scopeOutsourcedMissingSupplier: 'Proceso externalizado sin proveedor vinculado',
      aiProcessingWithoutConsent: 'Procesamiento IA sin política de consentimiento',
      metricReadingOverdue: 'Lectura de métrica vencida',
    },
    // Risk posture
    riskPosture: 'Postura de riesgo', noRisks: 'Sin riesgos registrados',
    high: 'Alto', medium: 'Medio', low: 'Bajo', treated: 'tratados',
    viewRisks: 'Gestionar riesgos →',
    // Open NCs
    openNcsTitle: 'No conformidades', noNcs: 'Sin NCs abiertas',
    openLabel: 'Abiertas', closedLabel: 'Cerradas', overdueActionsLabel: 'Acciones vencidas',
    viewNcs: 'Ver no conformidades →',
    // Deadlines
    deadlinesTitle: 'Vencimientos próximos (60 días)', noDeadlines: 'Sin vencimientos en 60 días',
    deadlinesBandNear: 'Próximos 30 días', deadlinesBandFar: 'Próximos 60 días',
    showAll: 'Ver todos', showLess: 'Ver menos',
    deadlineTypes: {
      rtp: 'RTP', audit: 'Auditoría', review: 'Revisión', nc: 'NC',
      objective: 'Objetivo', supplier: 'Proveedor', customer: 'Cliente', document: 'Documento',
      recert: 'Recertificación', stage1: 'Fase 1', stage2: 'Fase 2',
      risk: 'Tratamiento de riesgo', riskReview: 'Revisión de riesgo',
      mrAction: 'Acción de dirección', mrDecision: 'Decisión de dirección',
      metric: 'Lectura de métrica',
      legalReview: 'Revisión de obligación legal',
      scopeReview: 'Revisión de alcance', contextReview: 'Revisión de contexto',
      supplierContract: 'Vencimiento de contrato', competenceAction: 'Acción de competencia',
      awarenessActivity: 'Actividad de concienciación',
    },
    deadlineFallbacks: {
      audit: '[Sin referencia]',
      review: '[Sin referencia]',
      nc: '[Acción sin descripción]',
      objective: '[Objetivo sin título]',
      supplier: '[Proveedor sin nombre]',
      customer: '[Cliente sin nombre]',
      document: '[Documento sin título]',
      risk: '[Riesgo sin resumen]',
      riskReview: '[Riesgo sin resumen]',
      mrAction: '[Acción sin descripción]',
      mrDecision: '[Decisión sin texto]',
      rtp: '[Entrada RTP huérfana]',
      legalReview: '[Registro legal sin título]',
      metric: '[Métrica sin nombre]',
      supplierContract: '[Proveedor sin nombre]',
      competenceAction: '[Acción sin título]',
      awarenessActivity: '[Actividad sin título]',
    },
    // Control gap
    controlGapTitle: 'Brecha de implementación de controles',
    noControls: 'Sin controles aplicables',
    implLabel: 'Implementados', partialLabel: 'Parcial', notImplLabel: 'No implementados',
    applicableLabel: 'controles aplicables', viewSoa: 'Ver declaración de aplicabilidad →',
    annexATitle: 'Implementación de controles — Anexo A',
    annexA: {
      a5: 'A.5 Organizacional (37)',
      a6: 'A.6 Personas (8)',
      a7: 'A.7 Físicos (14)',
      a8: 'A.8 Tecnológicos (34)',
    },
    // Compliance score
    complianceTitle: 'Cumplimiento del SGSI',
    complianceHardGateNote: 'Limitado a 60 — requisitos clave incompletos',
    clauseLabels: {
      cl4: 'Cl. 4 Contexto',
      cl5: 'Cl. 5 Liderazgo',
      cl6: 'Cl. 6 Planificación',
      cl7: 'Cl. 7 Soporte',
      cl8: 'Cl. 8 Operación',
      cl9: 'Cl. 9 Evaluación',
      cl10: 'Cl. 10 Mejora',
    },
    trackedCoverageNote: 'Cobertura verificada: {pct}% de los requisitos ISO 27001',
    // Dashboard configuration
    configureDashboard: 'Configurar panel',
    configureTitle: 'Paneles visibles',
    closeConfig: 'Cerrar',
    panelLabels: {
      healthScore: 'Salud del SGSI',
      riskPosture: 'Postura de riesgo',
      nonconformities: 'No conformidades',
      alerts: 'Alertas de inteligencia',
      riskGaps: 'Brechas en riesgos',
      documentGaps: 'Brechas en documentos',
      orphanRefs: 'Referencias huérfanas',
      deadlines: 'Vencimientos próximos',
      controlGap: 'Brecha de controles',
      annexA: 'Implementación Anexo A',
      complianceScore: 'Cumplimiento del SGSI',
      ismsProgress: 'Progreso del SGSI',
    },
    riskGapsTitle: 'Brechas en riesgos',
    riskGapsNone: 'Sin brechas detectadas',
    documentGapsTitle: 'Brechas en documentos',
    documentGapsNone: 'Sin brechas detectadas',
    riskGapLabels: {
      highRiskAllControlsNotImplemented: 'Riesgo alto con todos los controles sin implementar',
      modifyWithoutLinkedControls: 'Tratamiento "Modificar" sin controles vinculados',
      treatmentCompletedButResidualHigh: 'Tratamiento completado pero riesgo residual ALTO',
      residualExceedsInherent: 'Puntuación residual > inherente (error de datos)',
      acceptedWithoutCriteria: 'Riesgo aceptado sin criterios de aceptación',
      approverEqualsOwner: 'Aprobador del tratamiento = propietario (segregación de funciones)',
    },
    documentGapLabels: {
      approvedWithoutEvidence: 'Documento aprobado sin evidencia de aprobación',
      orphanNoClauseRefsNoControls: 'Documento aprobado sin cláusulas ISO ni controles vinculados',
    },
    orphanRefsTitle: 'Referencias huérfanas',
    orphanRefsNone: 'Sin referencias huérfanas',
    orphanRefsCount: '{count} referencias huérfanas detectadas',
    holderTypeLabels: {
      risk: 'Riesgo', asset: 'Activo', supplier: 'Proveedor', customer: 'Cliente', incident: 'Incidente',
      audit: 'Auditoría', nonconformity: 'No conformidad', objective: 'Objetivo',
      metric: 'Métrica', document: 'Documento',
      'management-review': 'Revisión por dirección', 'legal-register': 'Registro legal',
      'soa-control': 'Control SoA', scope: 'Alcance',
    },
    openHolder: 'Abrir →',
  },
  en: {
    executiveDashboard: 'Executive Dashboard',
    sector: 'Sector', country: 'Country',
    switchOrg: 'Switch organization', settings: 'Settings',
    statusEmpty: 'Empty', statusInProgress: 'In progress', statusComplete: 'Complete',
    modulesTitle: 'ISMS Modules', comingSoon: 'Coming soon',
    certStatusLabel: 'Certification',
    certStatusLabels: {
      none: 'Not certified', preparing: 'Preparing', stage1: 'Stage 1', stage2: 'Stage 2',
      certified: 'Certified', surveillance: 'Surveillance', expired: 'Expired',
    },
    pdcaLabel: 'PDCA Cycle',
    pdcaStages: { Plan: 'Plan', Do: 'Do', Check: 'Check', Act: 'Act' },
    nextMilestone: 'Next milestone',
    daysRemaining: 'Days remaining',
    healthScoreTitle: 'ISMS Health Score',
    hardGateNote: 'Score capped at 60 — key documents pending approval',
    viewBreakdown: 'View breakdown', hideBreakdown: 'Hide breakdown',
    loading: 'Calculating…',
    componentLabels: {
      riskTreatment: 'Risk treatment', controlImplementation: 'Control implementation',
      objectiveAchievement: 'Objective achievement', auditCadence: 'Audit cadence',
      mrCadence: 'Management reviews', ncClosure: 'NC closure',
      documentCurrency: 'Document currency', awarenessCoverage: 'Awareness coverage',
      competenceCompletion: 'Competence', supplierReviewCadence: 'Supplier reviews',
      assetInventoryHealth: 'Asset inventory',
    },
    alertsTitle: 'Intelligence alerts',
    alertsNone: 'No active alerts',
    showSnoozed: 'Show snoozed', hideSnoozed: 'Hide snoozed',
    snoozedUntil: 'until',
    snooze: 'Snooze', snooze7: '7 days', snooze30: '30 days', snooze90: '90 days', reactivate: 'Reactivate', snoozeClose: 'Close',
    snoozedCasesLabel: 'Snoozed',
    severityLabels: { critical: 'Critical', high: 'High', medium: 'Medium', low: 'Low' },
    alertTitles: {
      highRisksUntreated: 'Untreated high risks',
      gdprNotificationOverdue: 'GDPR notification overdue (>72h)',
      nis2EarlyWarningOverdue: 'NIS2 early warning overdue (>24h)',
      nis2NotificationOverdue: 'NIS2 notification overdue (>72h)',
      nis2FinalReportOverdue: 'NIS2 final report overdue (>30d)',
      majorNcFindingWithoutRecord: 'Audit finding without NC record',
      repeatFinding: 'Open repeat finding',
      certificationExpired: 'Certification expired',
      certificationExpiring180: 'Certification expiring <180 days',
      certificationExpiring90: 'Certification expiring <90 days',
      residualRiskNotAccepted: 'Residual risk not accepted after treatment',
      controlGapOnHighRisk: 'Control gap on high risk',
      overdueRtpActions: 'RTP actions past deadline',
      treatmentDeadlineMissing: 'Treatment action without deadline',
      openNcsOverdue: 'NC with overdue corrective action',
      ncClosedWithoutVerifier: 'NC closed without verifier',
      policyNotApproved: 'Information security policy not approved',
      policyApprovedWithoutEvidence: 'Policy approved without evidence',
      policyOverdueForReview: 'Policy overdue for review',
      soaNotApproved: 'SoA not approved',
      rtpNotApproved: 'RTP not approved',
      scopeNotApproved: 'Scope not approved',
      soaApprovedWithoutEvidence: 'SoA approved without evidence',
      rtpApprovedWithoutEvidence: 'RTP approved without evidence',
      scopeApprovedWithoutEvidence: 'Scope approved without evidence',
      topManagementCommitmentMissing: 'Top management commitment evidence missing',
      noInternalAuditWithinCycle: 'No internal audit within cycle',
      noManagementReviewWithinCycle: 'No management review within cycle',
      mrWithoutTopManagement: 'Management review without top management',
      mrWithEmptyRequiredInput: 'Management review missing required inputs',
      mrWithoutDecisions: 'Management review without decisions',
      mrWithDecisionsButNoActions: 'MR decisions without improvement actions',
      supplierGdprGap: 'Supplier processes personal data without DPA',
      customerGdprGap: 'Customer personal data processed without DPA',
      incidentsWithoutNc: 'Critical/high incident without NC',
      riskAcceptedWithoutApprover: 'Risk accepted without approver',
      personInactiveInOwnerRoles: 'Inactive person in owner role',
      contextReviewOverdue: 'Context review overdue (>12 months)',
      documentsOverdueForReview: 'Documents overdue for review',
      documentApprovedWithoutApproverOrDate: 'Approved document without approver or approval date',
      objectivesAtRisk: 'Objectives at risk or not achieved',
      objectivesDeadlineOverdue: 'Objectives past deadline',
      objectivesWithoutMeasurement: 'Objectives without measurement method',
      supplierReviewsOverdue: 'Supplier reviews overdue',
      supplierCriticalWithoutExitPlan: 'Critical supplier without exit plan',
      supplierCriticalWithExpiredContract: 'Critical supplier with expired contract',
      incidentRootCauseMissing: 'Incident without root cause analysis (>30d)',
      ncWithoutRootCauses: 'NCs without identified root causes',
      climateChangeNotAssessed: 'Climate change not assessed in context',
      riskHighWithoutOwner: 'High risk without owner',
      riskReviewOverdue: 'Risk review overdue',
      legalRegisterReviewOverdue: 'Legal register review overdue',
      awarenessCompetenceNoProgramme: 'No awareness/competence program this year',
      awarenessCoverageBelowTarget: 'Awareness coverage below target',
      certificationStatusStale: 'Certification status stale',
      evidenceRetentionExpiring: 'Evidence retention expiring <90 days',
      stage1ImminentWithGaps: 'Stage 1 audit imminent with documentation gaps',
      scopeOutsourcedMissingSupplier: 'Outsourced process without linked supplier',
      aiProcessingWithoutConsent: 'AI processing without consent policy',
      metricReadingOverdue: 'Metric reading overdue',
    },
    riskPosture: 'Risk posture', noRisks: 'No risks recorded',
    high: 'High', medium: 'Medium', low: 'Low', treated: 'treated',
    viewRisks: 'Manage risks →',
    openNcsTitle: 'Nonconformities', noNcs: 'No open NCs',
    openLabel: 'Open', closedLabel: 'Closed', overdueActionsLabel: 'Overdue actions',
    viewNcs: 'View nonconformities →',
    deadlinesTitle: 'Upcoming deadlines (60 days)', noDeadlines: 'No deadlines in the next 60 days',
    deadlinesBandNear: 'Next 30 days', deadlinesBandFar: 'Next 60 days',
    showAll: 'Show all', showLess: 'Show less',
    deadlineTypes: {
      rtp: 'RTP', audit: 'Audit', review: 'Review', nc: 'NC',
      objective: 'Objective', supplier: 'Supplier', customer: 'Customer', document: 'Document',
      recert: 'Recertification', stage1: 'Stage 1', stage2: 'Stage 2',
      risk: 'Risk treatment', riskReview: 'Risk review',
      mrAction: 'MR action', mrDecision: 'MR decision',
      metric: 'Metric reading',
      legalReview: 'Legal obligation review',
      scopeReview: 'Scope review', contextReview: 'Context review',
      supplierContract: 'Contract expiry', competenceAction: 'Competence action',
      awarenessActivity: 'Awareness activity',
    },
    deadlineFallbacks: {
      audit: '[No reference]',
      review: '[No reference]',
      nc: '[Action without description]',
      objective: '[Objective without title]',
      supplier: '[Supplier without name]',
      customer: '[Customer without name]',
      document: '[Document without title]',
      risk: '[Risk without summary]',
      riskReview: '[Risk without summary]',
      mrAction: '[Action without description]',
      mrDecision: '[Decision without text]',
      rtp: '[Orphan RTP entry]',
      legalReview: '[Legal record without title]',
      metric: '[Metric without name]',
      supplierContract: '[Supplier without name]',
      competenceAction: '[Action without title]',
      awarenessActivity: '[Activity without title]',
    },
    controlGapTitle: 'Control implementation gap',
    noControls: 'No applicable controls',
    implLabel: 'Implemented', partialLabel: 'Partial', notImplLabel: 'Not implemented',
    applicableLabel: 'applicable controls', viewSoa: 'View statement of applicability →',
    annexATitle: 'Control implementation — Annex A',
    annexA: {
      a5: 'A.5 Organizational (37)',
      a6: 'A.6 People (8)',
      a7: 'A.7 Physical (14)',
      a8: 'A.8 Technological (34)',
    },
    // Compliance score
    complianceTitle: 'ISMS Compliance Score',
    complianceHardGateNote: 'Capped at 60 — key requirements incomplete',
    clauseLabels: {
      cl4: 'Cl. 4 Context',
      cl5: 'Cl. 5 Leadership',
      cl6: 'Cl. 6 Planning',
      cl7: 'Cl. 7 Support',
      cl8: 'Cl. 8 Operation',
      cl9: 'Cl. 9 Evaluation',
      cl10: 'Cl. 10 Improvement',
    },
    trackedCoverageNote: 'Tracked coverage: {pct}% of ISO 27001 requirements',
    // Dashboard configuration
    configureDashboard: 'Configure dashboard',
    configureTitle: 'Visible panels',
    closeConfig: 'Close',
    panelLabels: {
      healthScore: 'ISMS Health Score',
      riskPosture: 'Risk Posture',
      nonconformities: 'Nonconformities',
      alerts: 'Intelligence Alerts',
      riskGaps: 'Risk gaps',
      documentGaps: 'Document gaps',
      orphanRefs: 'Orphan references',
      deadlines: 'Upcoming Deadlines',
      controlGap: 'Control Implementation Gap',
      annexA: 'Annex A Implementation',
      complianceScore: 'ISMS Compliance Score',
      ismsProgress: 'ISMS Progress',
    },
    riskGapsTitle: 'Risk gaps',
    riskGapsNone: 'No gaps detected',
    documentGapsTitle: 'Document gaps',
    documentGapsNone: 'No gaps detected',
    riskGapLabels: {
      highRiskAllControlsNotImplemented: 'High risk with all linked controls Not Implemented',
      modifyWithoutLinkedControls: '"Modify" treatment without linked controls',
      treatmentCompletedButResidualHigh: 'Treatment completed but residual risk still HIGH',
      residualExceedsInherent: 'Residual score > inherent (data entry error)',
      acceptedWithoutCriteria: 'Accepted risk without acceptance criteria',
      approverEqualsOwner: 'Treatment approver = owner (segregation of duties)',
    },
    documentGapLabels: {
      approvedWithoutEvidence: 'Approved document without approval evidence',
      orphanNoClauseRefsNoControls: 'Approved document without ISO clauses or linked controls',
    },
    orphanRefsTitle: 'Orphan references',
    orphanRefsNone: 'No orphan references',
    orphanRefsCount: '{count} orphan references detected',
    holderTypeLabels: {
      risk: 'Risk', asset: 'Asset', supplier: 'Supplier', customer: 'Customer', incident: 'Incident',
      audit: 'Audit', nonconformity: 'Nonconformity', objective: 'Objective',
      metric: 'Metric', document: 'Document',
      'management-review': 'Management review', 'legal-register': 'Legal register',
      'soa-control': 'SoA control', scope: 'Scope',
    },
    openHolder: 'Open →',
  },
};

// ─── HELPERS ──────────────────────────────────────────────────────────────────
function scoreColor(score) {
  if (score >= 80) return BRAND_PRIMARY;
  if (score >= 60) return STATUS_WARN.text;
  if (score >= 40) return '#d97706';
  return FIELD_ERROR_BORDER; // TODO: P6b — verify color role
}

function computeTrend(history) {
  if (!Array.isArray(history) || history.length < 2) return null;
  const thirtyDaysAgo = new Date(Date.now() - 30 * MS_PER_DAY);
  const baseline = [...history].reverse().find(e => new Date(e.timestamp) <= thirtyDaysAgo);
  if (!baseline) return null;
  return Math.round(history[history.length - 1].score - baseline.score);
}

function getNextMilestone(org) {
  const cs = org.certificationStatus;
  if (cs === 'preparing' && org.stage1AuditDate) return { label: { es: 'Auditoría Fase 1', en: 'Stage 1 audit' }, date: org.stage1AuditDate };
  if ((cs === 'stage1' || cs === 'stage2') && org.stage2AuditDate) return { label: { es: 'Auditoría Fase 2', en: 'Stage 2 audit' }, date: org.stage2AuditDate };
  if ((cs === 'certified' || cs === 'surveillance') && org.recertificationDate) return { label: { es: 'Recertificación', en: 'Recertification' }, date: org.recertificationDate };
  return null;
}

function fmtDate(dateStr, lang) {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch { return dateStr; }
}

function maxUpdatedAt(items) {
  const dates = items.map(i => i.updatedAt).filter(Boolean).sort();
  return dates.length > 0 ? dates[dates.length - 1] : null;
}

// ─── COLOR TABLES ─────────────────────────────────────────────────────────────
const CERT_STATUS_COLORS = {
  none:         { bg: BG_SUBTLE,  text: TEXT_MUTED, border: BORDER_DEFAULT },
  preparing:    { bg: '#fffbeb',  text: STATUS_WARN.text, border: '#fcd34d' },
  stage1:       { bg: '#fffbeb',  text: STATUS_WARN.text, border: '#fcd34d' },
  stage2:       { bg: '#fffbeb',  text: STATUS_WARN.text, border: '#fcd34d' },
  certified:    { bg: '#f0fdf4',  text: BRAND_PRIMARY, border: '#86efac' },
  surveillance: { bg: '#f0fdf4',  text: BRAND_PRIMARY, border: '#86efac' },
  expired:      { bg: ERROR_BG,  text: FIELD_ERROR_BORDER, border: ERROR_BORDER }, // TODO: P6b — verify color role
};

const STATUS_COLORS = {
  empty:         { dot: '#d1d5db', text: TEXT_MUTED },
  'in-progress': { dot: '#f59e0b', text: STATUS_WARN.text },
  complete:      { dot: BRAND_PRIMARY, text: BRAND_PRIMARY },
};

const HOLDER_TYPE_TO_MODULE = {
  risk: 'risks', asset: 'assets', supplier: 'suppliers', incident: 'incidents',
  audit: 'audits', nonconformity: 'nonconformities', objective: 'objectives',
  metric: 'metrics', document: 'documents',
  'management-review': 'management-reviews', 'legal-register': 'legal-register',
  'soa-control': 'soa', scope: 'scope',
};

// ─── SUB-COMPONENTS ───────────────────────────────────────────────────────────
function SkeletonCard({ height }) {
  return (
    <div style={{ height: height || 80, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '0.55rem' }}>
      {[70, 50, 35].map((w, i) => (
        <div key={i} style={{ height: 11, borderRadius: 5, background: '#e5e3df', width: `${w}%` }} />
      ))}
    </div>
  );
}

// Single active case row inside a multi-entity alert breakdown. Full-row clickable
// button that deep-links to its entity (no navigation arrow — the whole row is
// clickable). At its right edge a per-case "Posponer" control mirrors the header
// snooze control but operates at the case level via onSnoozeCase(alertKey, cs.id, days).
// Every interactive element is a span with role=button (never a nested <button>) so
// the HTML stays valid inside the clickable row, and each stops propagation so
// navigating the row never fires when the snooze control is used. Reveal state is
// local to this row.
function CaseRow({ cs, t, onNavigate, alertKey, onSnoozeCase, snoozeLink }) {
  const [showSnoozeOpts, setShowSnoozeOpts] = useState(false);
  const caseSnoozeControl = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', flexShrink: 0 }}>
      {showSnoozeOpts ? (
        <>
          {[7, 30, 90].flatMap((days, i) => {
            const choice = (
              <span role="button" tabIndex={0} key={days} style={snoozeLink}
                onClick={(e) => { e.stopPropagation(); onSnoozeCase(alertKey, cs.id, days); }}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onSnoozeCase(alertKey, cs.id, days); } }}
              >
                {days === 7 ? t.snooze7 : days === 30 ? t.snooze30 : t.snooze90}
              </span>
            );
            return i === 0 ? [choice] : [
              <span aria-hidden="true" key={`sep-${days}`} style={{ ...FONT_TINY, color: TEXT_DISABLED }}>·</span>,
              choice,
            ];
          })}
          <span role="button" tabIndex={0} aria-label={t.snoozeClose} style={snoozeLink}
            onClick={(e) => { e.stopPropagation(); setShowSnoozeOpts(false); }}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); setShowSnoozeOpts(false); } }}
          >
            ✕
          </span>
        </>
      ) : (
        <span role="button" tabIndex={0} style={snoozeLink}
          onClick={(e) => { e.stopPropagation(); setShowSnoozeOpts(true); }}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); setShowSnoozeOpts(true); } }}
        >
          {t.snooze}
        </span>
      )}
    </div>
  );

  return (
    <button type="button"
      onClick={() => onNavigate(cs.module, cs.navParams ? cs.navParams : { editId: cs.id })}
      onMouseEnter={e => { e.currentTarget.style.background = BG_HOVER_LT; }}
      onMouseLeave={e => { e.currentTarget.style.background = BG_TABLE; }}
      onFocus={e => { e.currentTarget.style.outline = `2px solid ${BRAND_PRIMARY}`; e.currentTarget.style.outlineOffset = '2px'; }}
      onBlur={e => { e.currentTarget.style.outline = 'none'; e.currentTarget.style.outlineOffset = '0'; }}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: '0.6rem', padding: '0.45rem 0.65rem',
        background: BG_TABLE, border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 7,
        width: '100%', textAlign: 'left', cursor: 'pointer',
        font: 'inherit', color: 'inherit',
      }}
    >
      <span style={{
        ...FONT_SMALL, color: TEXT_DEFAULT,
        flex: 1, minWidth: 0,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {cs.label}
      </span>
      {caseSnoozeControl}
    </button>
  );
}

function AlertRow({ alert, t, lang, onNavigate, isOpen, onToggle, onSnooze, caseSnoozes, onSnoozeCase, onClearSnoozeCase }) {
  const c = SEV_COLORS[alert.severity] || SEV_COLORS.low;
  const title = (t.alertTitles && t.alertTitles[alert.key]) || alert.key;
  const hasCases = Array.isArray(alert.cases) && alert.cases.length > 0;
  const [showSnoozeOpts, setShowSnoozeOpts] = useState(false);
  const [snoozedSectionOpen, setSnoozedSectionOpen] = useState(false);

  // Snooze control — rendered once per alert header (never on individual case rows).
  // Reuses the muted-tiny / linkBtn vocabulary. stopPropagation keeps the click from
  // reaching the row's toggle (cases branch) or navigation (no-cases branch). Clicking
  // "Posponer" reveals the 7/30/90-day mini-controls inline; picking one snoozes the
  // alert via onSnooze, after which it drops from activeAlerts on the next render.
  const snoozeLink = {
    ...FONT_TINY,
    color: TEXT_DISABLED, cursor: 'pointer',
    background: 'none', border: 'none', padding: 0,
    textDecoration: 'underline', textDecorationColor: BORDER_DEFAULT,
    flexShrink: 0,
  };
  // The control transforms in place: collapsed it shows only the "Posponer"
  // affordance; expanded the word is replaced by the three duration choices
  // (middot-separated) plus a "✕" close affordance that collapses it again.
  // Every interactive span stops propagation so the header toggle/navigation
  // never fires, and keeps Enter/Space keyboard handling.
  const snoozeControl = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', flexShrink: 0 }}>
      {showSnoozeOpts ? (
        <>
          {[7, 30, 90].flatMap((days, i) => {
            const choice = (
              <span role="button" tabIndex={0} key={days} style={snoozeLink}
                onClick={(e) => { e.stopPropagation(); onSnooze(alert.key, days); }}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onSnooze(alert.key, days); } }}
              >
                {days === 7 ? t.snooze7 : days === 30 ? t.snooze30 : t.snooze90}
              </span>
            );
            return i === 0 ? [choice] : [
              <span aria-hidden="true" key={`sep-${days}`} style={{ ...FONT_TINY, color: TEXT_DISABLED }}>·</span>,
              choice,
            ];
          })}
          <span role="button" tabIndex={0} aria-label={t.snoozeClose} style={snoozeLink}
            onClick={(e) => { e.stopPropagation(); setShowSnoozeOpts(false); }}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); setShowSnoozeOpts(false); } }}
          >
            ✕
          </span>
        </>
      ) : (
        <span role="button" tabIndex={0} style={snoozeLink}
          onClick={(e) => { e.stopPropagation(); setShowSnoozeOpts(true); }}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); setShowSnoozeOpts(true); } }}
        >
          {t.snooze}
        </span>
      )}
    </div>
  );

  // Multi-entity alert with resolved cases → collapsible group replicating the
  // gap-panel pattern: the header toggles the breakdown (it does NOT navigate),
  // and each case below is a full-row clickable row that deep-links to its
  // entity. Keeps the per-severity colored background/border and the
  // brightness-filter hover AlertRow uses for its color background.
  if (hasCases) {
    // Partition the resolved cases into active and snoozed using the case-snooze
    // map. A case is snoozed while its composite "alertKey::entityId" key holds a
    // future date (same future-date test snoozedEntries applies to alertSnoozes);
    // otherwise it stays active. Order is preserved within each list. The header
    // count (alert.count) is intentionally NOT reduced — active + snoozed reconcile
    // to it visibly (a "(15)" alert with one snoozed case shows 14 active here).
    const nowDate = new Date();
    const activeCases = [];
    const snoozedCases = [];
    for (const cs of alert.cases) {
      const compositeKey = `${alert.key}::${cs.id}`;
      const until = caseSnoozes && caseSnoozes[compositeKey];
      if (until && new Date(until) > nowDate) snoozedCases.push({ cs, until });
      else activeCases.push(cs);
    }
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
        <button type="button"
          onClick={onToggle}
          onMouseEnter={e => { e.currentTarget.style.filter = 'brightness(0.97)'; }}
          onMouseLeave={e => { e.currentTarget.style.filter = 'none'; }}
          onFocus={e => { e.currentTarget.style.outline = `2px solid ${BRAND_PRIMARY}`; e.currentTarget.style.outlineOffset = '2px'; }}
          onBlur={e => { e.currentTarget.style.outline = 'none'; e.currentTarget.style.outlineOffset = '0'; }}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: '0.6rem', padding: '0.45rem 0.65rem',
            background: c.bg, border: `1px solid ${c.border}`, borderRadius: 7,
            width: '100%', textAlign: 'left', cursor: 'pointer',
            font: 'inherit', color: 'inherit',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', flex: 1, minWidth: 0 }}>
            <span style={{ ...FONT_TINY, color: c.text, flexShrink: 0 }}>{isOpen ? '▾' : '▸'}</span>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: c.dot, flexShrink: 0 }} />
            <span style={{
              ...FONT_SMALL, color: c.text,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {title}{alert.count > 1 ? ` (${alert.count})` : ''}
            </span>
          </div>
          {snoozeControl}
        </button>
        {isOpen && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', paddingLeft: '1.25rem' }}>
            {activeCases.map((cs, i) => (
              <CaseRow
                key={`${cs.id}-${i}`}
                cs={cs}
                t={t}
                onNavigate={onNavigate}
                alertKey={alert.key}
                onSnoozeCase={onSnoozeCase}
                snoozeLink={snoozeLink}
              />
            ))}
            {snoozedCases.length > 0 && (
              <>
                {/* Thin separator + collapsible "Pospuestos (N)" sub-section. Snoozed
                    cases are de-emphasized and non-navigable, each with a Reactivar
                    control that clears its composite "alertKey::entityId" key. Starts
                    collapsed; open state is local to this alert's rendering. */}
                <div style={{ borderTop: `1px solid ${BORDER_DEFAULT}`, margin: '0.15rem 0' }} />
                <button type="button"
                  onClick={() => setSnoozedSectionOpen(v => !v)}
                  style={{
                    ...FONT_TINY, color: TEXT_MUTED,
                    display: 'flex', alignItems: 'center', gap: '0.4rem',
                    background: 'none', border: 'none', padding: '0.15rem 0',
                    cursor: 'pointer', textAlign: 'left',
                  }}
                >
                  <span aria-hidden="true">{snoozedSectionOpen ? '▾' : '▸'}</span>
                  <span>{t.snoozedCasesLabel} ({snoozedCases.length})</span>
                </button>
                {snoozedSectionOpen && snoozedCases.map(({ cs, until }, i) => (
                  <div key={`${cs.id}-snoozed-${i}`} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    gap: '0.5rem', padding: '0.3rem 0.5rem',
                  }}>
                    <span style={{
                      ...FONT_TINY, color: TEXT_DISABLED,
                      flex: 1, minWidth: 0,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {cs.label}
                    </span>
                    <span style={{ ...FONT_TINY, color: TEXT_DISABLED, flexShrink: 0 }}>
                      {t.snoozedUntil} {fmtDate(until, lang)}
                    </span>
                    <button type="button" style={snoozeLink}
                      onClick={() => onClearSnoozeCase(`${alert.key}::${cs.id}`)}
                    >
                      {t.reactivate}
                    </button>
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </div>
    );
  }

  const alertModule = (ALERT_CATALOG[alert.key] || {}).module;
  const alertSection = (ALERT_CATALOG[alert.key] || {}).section;
  const innerContent = (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', flex: 1, minWidth: 0 }}>
        {/* Reserve the disclosure-triangle slot (DM Mono is monospace, so a hidden
            glyph occupies the exact width the ▸/▾ takes on cases headers) so the
            severity dot aligns into a column across all alert rows. */}
        <span style={{ ...FONT_TINY, flexShrink: 0, visibility: 'hidden' }}>▸</span>
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: c.dot, flexShrink: 0 }} />
        <span style={{
          ...FONT_SMALL, color: c.text,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {title}{alert.count > 1 ? ` (${alert.count})` : ''}
        </span>
      </div>
      {snoozeControl}
    </>
  );

  if (alertModule) {
    return (
      <button type="button"
        onClick={() => alertSection ? onNavigate(alertModule, { section: alertSection }) : onNavigate(alertModule)}
        onMouseEnter={e => { e.currentTarget.style.filter = 'brightness(0.97)'; }}
        onMouseLeave={e => { e.currentTarget.style.filter = 'none'; }}
        onFocus={e => { e.currentTarget.style.outline = `2px solid ${BRAND_PRIMARY}`; e.currentTarget.style.outlineOffset = '2px'; }}
        onBlur={e => { e.currentTarget.style.outline = 'none'; e.currentTarget.style.outlineOffset = '0'; }}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: '0.6rem', padding: '0.45rem 0.65rem',
          background: c.bg, border: `1px solid ${c.border}`, borderRadius: 7,
          width: '100%', textAlign: 'left', cursor: 'pointer',
          font: 'inherit', color: 'inherit',
        }}
      >
        {innerContent}
      </button>
    );
  }

  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: '0.6rem', padding: '0.45rem 0.65rem',
      background: c.bg, border: `1px solid ${c.border}`, borderRadius: 7,
    }}>
      {innerContent}
    </div>
  );
}

function DeadlineRow({ item, lang, t, onNavigate }) {
  const daysUntil = Math.ceil((new Date(item.date) - new Date()) / MS_PER_DAY);
  const urgent = daysUntil <= 7;
  const displayLabel = (typeof item.label === 'string' && item.label)
    ? item.label
    : (t.deadlineFallbacks?.[item.type] || '');
  const innerContent = (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1, minWidth: 0 }}>
        <span style={{
          ...FONT_TINY, color: TEXT_DISABLED,
          flexShrink: 0, textTransform: 'uppercase', letterSpacing: '0.04em',
        }}>
          {(t.deadlineTypes && t.deadlineTypes[item.type]) || item.type}
        </span>
        <span style={{
          ...FONT_LIST_ITEM, color: TEXT_HEADING,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {displayLabel}
        </span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexShrink: 0 }}>
        <span style={{
          ...FONT_SMALL,
          color: urgent ? STATUS_WARN.text : TEXT_MUTED, fontWeight: urgent ? 500 : 400,
        }}>
          {fmtDate(item.date, lang)}
        </span>
      </div>
    </>
  );

  if (item.module) {
    const baseBg = urgent ? '#fffbeb' : BG_SURFACE;
    return (
      <button type="button"
        onClick={() => onNavigate(item.module)}
        onMouseEnter={e => {
          if (urgent) e.currentTarget.style.filter = 'brightness(0.97)';
          else e.currentTarget.style.background = BG_HOVER_LT;
        }}
        onMouseLeave={e => {
          e.currentTarget.style.filter = 'none';
          e.currentTarget.style.background = baseBg;
        }}
        onFocus={e => { e.currentTarget.style.outline = `2px solid ${BRAND_PRIMARY}`; e.currentTarget.style.outlineOffset = '2px'; }}
        onBlur={e => { e.currentTarget.style.outline = 'none'; e.currentTarget.style.outlineOffset = '0'; }}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: '0.75rem', padding: '0.45rem 0.65rem',
          background: baseBg,
          border: `1px solid ${urgent ? '#fcd34d' : BORDER_DEFAULT}`, borderRadius: 7,
          width: '100%', textAlign: 'left', cursor: 'pointer',
          font: 'inherit', color: 'inherit',
        }}
      >
        {innerContent}
      </button>
    );
  }

  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: '0.75rem', padding: '0.45rem 0.65rem',
      background: urgent ? '#fffbeb' : BG_SURFACE,
      border: `1px solid ${urgent ? '#fcd34d' : BORDER_DEFAULT}`, borderRadius: 7,
    }}>
      {innerContent}
    </div>
  );
}

// ─── DASHBOARD ────────────────────────────────────────────────────────────────
export default function Dashboard({ lang, org, onSwitchOrg, onNavigate, onOrgUpdated }) {
  const [statuses, setStatuses] = useState({});
  const [moduleUpdatedAt, setModuleUpdatedAt] = useState({});
  const [alerts, setAlerts] = useState([]);
  const [healthScore, setHealthScore] = useState(null);
  const [scoreComponents, setScoreComponents] = useState(null);
  const [hardGateFired, setHardGateFired] = useState(false);
  const [intelligenceLoading, setIntelligenceLoading] = useState(true);
  const [showScoreBreakdown, setShowScoreBreakdown] = useState(false);
  const [showSnoozed, setShowSnoozed] = useState(false);
  const [showAllDeadlines, setShowAllDeadlines] = useState(false);
  const [alertsExpanded, setAlertsExpanded] = useState(false);
  const [openAlertGroups, setOpenAlertGroups] = useState(() => new Set());
  const [riskGaps, setRiskGaps] = useState([]);
  const [openRiskGapGroups, setOpenRiskGapGroups] = useState(() => new Set());
  const [documentGaps, setDocumentGaps] = useState([]);
  const [openDocumentGapGroups, setOpenDocumentGapGroups] = useState(() => new Set());
  const [orphans, setOrphans] = useState([]);
  const [orphansExpanded, setOrphansExpanded] = useState(false);
  const [riskCounts, setRiskCounts] = useState(null);
  const [ncCounts, setNcCounts] = useState(null);
  const [controlGap, setControlGap] = useState(null);
  const [annexAGap, setAnnexAGap] = useState(null);
  const [upcomingDeadlines, setUpcomingDeadlines] = useState([]);
  const [winWidth, setWinWidth] = useState(window.innerWidth);
  const [complianceResult, setComplianceResult] = useState(null);
  const [dashConfig, setDashConfig] = useState(() => {
    try {
      const stored = localStorage.getItem('grc-dashboard-config');
      if (stored) return { ...DEFAULT_DASH_CONFIG, ...JSON.parse(stored) };
    } catch { /* fall through */ }
    return DEFAULT_DASH_CONFIG;
  });
  const [showDashConfig, setShowDashConfig] = useState(false);

  const togglePanel = (key) => {
    setDashConfig(prev => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem('grc-dashboard-config', JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };

  // Toggle a single gap-type group open/closed within a gap panel. Per-panel state
  // is a Set of open gap keys; expand-all / collapse-all is handled inline at the
  // panel header by replacing the whole Set.
  const toggleGapGroup = (setter, key) => {
    setter(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const t = T[lang] || T.en;
  const { id: orgId } = org;

  // Snooze persistence — write/clear org.alertSnoozes and persist profile.json via the
  // same writeOrgFile + onOrgUpdated path the health-score effect uses. Only the
  // alertSnoozes field is mutated on the profile copy. activeAlerts/snoozedEntries
  // already react to the written map on the next render.
  const handleSnoozeAlert = async (alertKey, days) => {
    const snoozeUntil = new Date(Date.now() + days * MS_PER_DAY).toISOString();
    const updatedProfile = {
      ...org,
      alertSnoozes: { ...(org.alertSnoozes || {}), [alertKey]: snoozeUntil },
    };
    await writeOrgFile(orgId, 'profile.json', updatedProfile);
    if (onOrgUpdated) onOrgUpdated(updatedProfile);
  };

  const handleClearSnooze = async (alertKey) => {
    const nextSnoozes = { ...(org.alertSnoozes || {}) };
    delete nextSnoozes[alertKey];
    const updatedProfile = { ...org, alertSnoozes: nextSnoozes };
    await writeOrgFile(orgId, 'profile.json', updatedProfile);
    if (onOrgUpdated) onOrgUpdated(updatedProfile);
  };

  // Case-level snooze persistence — write/clear org.alertCaseSnoozes, a second,
  // independent map keyed by a composite "alertKey::entityId" string so a single case
  // can be silenced within one alert while the same entity still surfaces under any
  // other alert it triggers. These mirror handleSnoozeAlert/handleClearSnooze exactly
  // (shallow copy, writeOrgFile profile.json, onOrgUpdated) but mutate only
  // alertCaseSnoozes — never alertSnoozes or any other profile field.
  const handleSnoozeCase = async (alertKey, entityId, days) => {
    const snoozeUntil = new Date(Date.now() + days * MS_PER_DAY).toISOString();
    const compositeKey = `${alertKey}::${entityId}`;
    const updatedProfile = {
      ...org,
      alertCaseSnoozes: { ...(org.alertCaseSnoozes || {}), [compositeKey]: snoozeUntil },
    };
    await writeOrgFile(orgId, 'profile.json', updatedProfile);
    if (onOrgUpdated) onOrgUpdated(updatedProfile);
  };

  const handleClearSnoozeCase = async (compositeKey) => {
    const nextCaseSnoozes = { ...(org.alertCaseSnoozes || {}) };
    delete nextCaseSnoozes[compositeKey];
    const updatedProfile = { ...org, alertCaseSnoozes: nextCaseSnoozes };
    await writeOrgFile(orgId, 'profile.json', updatedProfile);
    if (onOrgUpdated) onOrgUpdated(updatedProfile);
  };

  useEffect(() => {
    const onResize = () => setWinWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // ── Intelligence + panel data ─────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setIntelligenceLoading(true);
    (async () => {
      try {
        const dlCurrentYear = new Date().getFullYear();
        const [
          hsResult, alertsResult, riskGapsResult, documentGapsResult, orphansResult,
          pRisks, pSoa, pNcs,
          pAudits, pDocs, pSuppliers, pCustomers, pMrs, pObjectives, pMetrics, pRtp,
          pLegal, pScope, pContext, pCompetence, pAwareness,
        ] = await Promise.all([
          computeHealthScore(orgId, org),
          computeAlerts(orgId, org),
          computeRiskGaps(orgId).catch(() => []),
          computeDocumentGaps(orgId).catch(() => []),
          computeOrphanRefs(orgId).catch(() => []),
          listOrgCollection(orgId, 'risks').catch(() => []),
          readOrgFile(orgId, 'controls/soa.json').catch(() => null),
          listOrgCollection(orgId, 'nonconformities').catch(() => []),
          listOrgCollection(orgId, 'audits').catch(() => []),
          listOrgCollection(orgId, 'documents').catch(() => []),
          listOrgCollection(orgId, 'suppliers').catch(() => []),
          listOrgCollection(orgId, 'customers').catch(() => []),
          listOrgCollection(orgId, 'management-reviews').catch(() => []),
          listOrgCollection(orgId, 'objectives').catch(() => []),
          listOrgCollection(orgId, 'metrics').catch(() => []),
          readOrgFile(orgId, 'rtp.json').catch(() => null),
          listOrgCollection(orgId, 'legal-register').catch(() => []),
          readOrgFile(orgId, 'scope.json').catch(() => null),
          readOrgFile(orgId, 'context.json').catch(() => null),
          readOrgFile(orgId, `competence/program-${dlCurrentYear}.json`).catch(() => null),
          readOrgFile(orgId, `awareness/program-${dlCurrentYear}.json`).catch(() => null),
        ]);

        if (cancelled) return;

        // persist health score history
        const nowIso = new Date().toISOString();
        const prevHistory = Array.isArray(org.healthScoreHistory) ? org.healthScoreHistory : [];
        const newEntry = { timestamp: nowIso, score: hsResult.score, components: hsResult.components };
        const updatedHistory = [...prevHistory.slice(-23), newEntry];
        const updatedProfile = { ...org, healthScore: hsResult.score, healthScoreUpdatedAt: nowIso, healthScoreHistory: updatedHistory };
        await writeOrgFile(orgId, 'profile.json', updatedProfile);

        if (cancelled) return;
        if (onOrgUpdated) onOrgUpdated(updatedProfile);

        setHealthScore(hsResult.score);
        setScoreComponents(hsResult.components);
        setHardGateFired(hsResult.hardGateFired);
        setAlerts(alertsResult);
        setRiskGaps(Array.isArray(riskGapsResult) ? riskGapsResult : []);
        setDocumentGaps(Array.isArray(documentGapsResult) ? documentGapsResult : []);
        setOrphans(Array.isArray(orphansResult) ? orphansResult : []);

        // risk counts
        setRiskCounts({
          high: pRisks.filter(r => r.riskLevel === 'HIGH').length,
          medium: pRisks.filter(r => r.riskLevel === 'MEDIUM').length,
          low: pRisks.filter(r => r.riskLevel === 'LOW').length,
          total: pRisks.length,
          treated: pRisks.filter(r => r.treatmentActionStatus === 'Completed' || r.accepted === true).length,
        });

        // nc counts
        const ncToday = new Date();
        setNcCounts({
          open: pNcs.filter(nc => nc.status !== 'Closed').length,
          closed: pNcs.filter(nc => nc.status === 'Closed').length,
          overdueActions: pNcs.filter(nc =>
            nc.status !== 'Closed' &&
            Array.isArray(nc.correctiveActions) &&
            nc.correctiveActions.some(ca => ca.deadline && new Date(ca.deadline) < ncToday)
          ).length,
        });

        // control gap
        const soaApplicable = pSoa ? (pSoa.controls || []).filter(c => c.applicable) : [];
        setControlGap({
          total: soaApplicable.length,
          implemented: soaApplicable.filter(c => c.implementationStatus === 'Implemented').length,
          partial: soaApplicable.filter(c => c.implementationStatus === 'Partially implemented').length,
          notImplemented: soaApplicable.filter(c => c.implementationStatus === 'Not implemented').length,
        });

        // Annex A gap by section
        if (pSoa) {
          const annexGap = {};
          for (const sec of ['5', '6', '7', '8']) {
            const applicable = (pSoa.controls || []).filter(c => {
              if (!c.applicable) return false;
              const rawId = (c.id || '').replace(/^iso27001:/, '');
              return rawId.startsWith(`${sec}.`);
            });
            annexGap[`a${sec}`] = {
              applicable: applicable.length,
              implemented: applicable.filter(c => c.implementationStatus === 'Implemented').length,
              partial: applicable.filter(c => c.implementationStatus === 'Partially implemented').length,
            };
          }
          setAnnexAGap(annexGap);
        }

        // upcoming deadlines
        const dlToday = new Date();
        const dlSixty = new Date(dlToday.getTime() + 60 * MS_PER_DAY);
        const dlList = [];
        const dlDay = (v) => { const x = new Date(v); return Date.UTC(x.getUTCFullYear(), x.getUTCMonth(), x.getUTCDate()); };
        const addDl = (date, type, label, module) => {
          if (!date) return;
          const d = dlDay(date);
          if (d >= dlDay(dlToday) && d <= dlDay(dlSixty)) dlList.push({ date, type, label, module });
        };

        for (const a of pAudits) {
          if (a.status !== 'Completed' && a.plannedDate) addDl(a.plannedDate, 'audit', a.ref, 'audits');
        }
        for (const mr of pMrs) {
          if (!mr.approvalDate && mr.nextMeetingDate) addDl(mr.nextMeetingDate, 'review', mr.ref, 'management-reviews');
        }
        for (const nc of pNcs) {
          if (nc.status !== 'Closed' && Array.isArray(nc.correctiveActions)) {
            for (const ca of nc.correctiveActions) {
              if (ca.status !== 'Completed' && ca.deadline) addDl(ca.deadline, 'nc', ca.description, 'nonconformities');
            }
          }
        }
        for (const o of pObjectives) {
          if (o.status !== 'Achieved' && o.deadline) addDl(o.deadline, 'objective', o.objective, 'objectives');
        }
        for (const s of pSuppliers) {
          if (s.nextReviewDate) addDl(s.nextReviewDate, 'supplier', s.name, 'suppliers');
        }
        for (const c of pCustomers) {
          if (c.nextReviewDate) addDl(c.nextReviewDate, 'customer', c.name, 'customers');
        }
        for (const d of pDocs) {
          if (d.nextReviewDate) addDl(d.nextReviewDate, 'document', d.title, 'documents');
        }
        addDl(org.recertificationDate, 'recert', 'ISO 27001', 'settings');
        addDl(org.stage1AuditDate, 'stage1', 'ISO 27001', 'settings');
        addDl(org.stage2AuditDate, 'stage2', 'ISO 27001', 'settings');

        for (const r of pRisks) {
          if (r.treatmentActionStatus !== 'Completed' && r.treatmentDeadline)
            addDl(r.treatmentDeadline, 'risk', r.riskSummary, 'risks');
        }
        for (const r of pRisks) {
          if (r.nextReviewDate)
            addDl(r.nextReviewDate, 'riskReview', r.riskSummary, 'risks');
        }
        for (const mr of pMrs) {
          for (const action of (Array.isArray(mr.actions) ? mr.actions : [])) {
            if (action.status !== 'Completed' && action.deadline)
              addDl(action.deadline, 'mrAction', action.description, 'management-reviews');
          }
        }
        for (const mr of pMrs) {
          for (const decision of (Array.isArray(mr.decisions) ? mr.decisions : [])) {
            if (decision.status !== 'Completed' && decision.dueDate)
              addDl(decision.dueDate, 'mrDecision', decision.text, 'management-reviews');
          }
        }
        if (pRtp && Array.isArray(pRtp.entries)) {
          for (const entry of pRtp.entries) {
            if (entry && entry.deadline) {
              const resolvedRisk = pRisks.find(rk => rk.id === entry.riskId);
              const rtpLabel = (resolvedRisk && resolvedRisk.riskSummary) ? resolvedRisk.riskSummary : '';
              addDl(entry.deadline, 'rtp', rtpLabel, 'risks');
            }
          }
        }
        for (const lr of pLegal) {
          if (lr.status !== 'Superseded' && lr.reviewDate) {
            addDl(lr.reviewDate, 'legalReview', lr.title, 'legal-register');
          }
        }
        {
          const metPeriods = { Monthly: 1, Quarterly: 3, Annual: 12 };
          const metAddMonths = (dateStr, n) => {
            const d = new Date(dateStr);
            const r = new Date(d.getFullYear(), d.getMonth() + n, d.getDate());
            if (r.getDate() !== d.getDate()) r.setDate(0);
            return r;
          };
          for (const met of pMetrics) {
            const readings = Array.isArray(met.readings) ? met.readings : [];
            let expectedNext;
            if (readings.length === 0) {
              const base = (met.updatedAt || '').slice(0, 10) || dlToday.toISOString().slice(0, 10);
              if (met.frequency === 'Weekly') {
                expectedNext = new Date(new Date(base).getTime() + 7 * MS_PER_DAY);
              } else {
                expectedNext = metAddMonths(base, metPeriods[met.frequency] || 1);
              }
            } else {
              const sorted = readings.slice().sort((a, b) => new Date(b.date) - new Date(a.date));
              const lastDate = sorted[0].date;
              if (met.frequency === 'Weekly') {
                expectedNext = new Date(new Date(lastDate).getTime() + 7 * MS_PER_DAY);
              } else {
                expectedNext = metAddMonths(lastDate, metPeriods[met.frequency] || 1);
              }
            }
            addDl(expectedNext.toISOString().slice(0, 10), 'metric', met.name, 'metrics');
          }
        }

        // Scope review (singleton): label sourced from the canonical Scope module name
        if (pScope && pScope.nextReviewDate) {
          const scopeLabel = (MODULES.find(m => m.id === 'scope') || {}).label;
          addDl(pScope.nextReviewDate, 'scopeReview', scopeLabel ? scopeLabel[lang] : '', 'scope');
        }
        // Context review (singleton): reviewDate + 12 calendar months — identical arithmetic to
        // the context-review overdue alert so the deadline date and the alert threshold coincide
        if (pContext && pContext.reviewDate) {
          const ctxDeadline = new Date(pContext.reviewDate);
          ctxDeadline.setMonth(ctxDeadline.getMonth() + 12);
          const contextLabel = (MODULES.find(m => m.id === 'context') || {}).label;
          addDl(ctxDeadline, 'contextReview', contextLabel ? contextLabel[lang] : '', 'context');
        }
        // Supplier contract expiry — second, independent deadline type for suppliers
        for (const s of pSuppliers) {
          if (s.status !== 'Terminated' && s.contractExpiry) addDl(s.contractExpiry, 'supplierContract', s.name, 'suppliers');
        }
        // Competence training-action deadlines (current-year programme)
        if (pCompetence && Array.isArray(pCompetence.roles)) {
          for (const role of pCompetence.roles) {
            for (const action of (Array.isArray(role.trainingActions) ? role.trainingActions : [])) {
              if (action.status !== 'Completed' && action.deadline)
                addDl(action.deadline, 'competenceAction', action.title, 'competence');
            }
          }
        }
        // Awareness activity planned dates (current-year programme)
        if (pAwareness && Array.isArray(pAwareness.activities)) {
          for (const activity of pAwareness.activities) {
            if (activity.status === 'Planned' && activity.plannedDate)
              addDl(activity.plannedDate, 'awarenessActivity', activity.title, 'awareness');
          }
        }

        dlList.sort((a, b) => new Date(a.date) - new Date(b.date));
        setUpcomingDeadlines(dlList);

      } catch { /* intelligence is non-critical */ }
      finally { if (!cancelled) setIntelligenceLoading(false); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  // ── Compliance score ──────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await computeComplianceScore(orgId, org);
        if (!cancelled) setComplianceResult(result);
      } catch { /* compliance score is non-critical */ }
      finally { /* no loading flag — null state renders the skeleton */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  // ── Module statuses ───────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const results = await Promise.all(
        MODULES.map(async (m) => {
          if (m.id === 'context') {
            try {
              const ctx = await readOrgFile(orgId, m.file);
              const hasAny = (ctx.internalIssues?.length > 0) || (ctx.externalIssues?.length > 0) || (ctx.interestedParties?.length > 0);
              if (!hasAny) return { id: m.id, status: 'empty', updatedAt: ctx.updatedAt || null };
              const complete = ctx.internalIssues?.length > 0 && ctx.externalIssues?.length > 0 && ctx.interestedParties?.length > 0 && ctx.reviewDate;
              return { id: m.id, status: complete ? 'complete' : 'in-progress', updatedAt: ctx.updatedAt || null };
            } catch { return { id: m.id, status: 'empty', updatedAt: null }; }
          }
          if (m.id === 'scope') {
            try {
              const scope = await readOrgFile(orgId, m.file);
              if (!scope.description) return { id: m.id, status: 'empty', updatedAt: scope.updatedAt || null };
              return { id: m.id, status: scope.approvalDate ? 'complete' : 'in-progress', updatedAt: scope.updatedAt || null };
            } catch { return { id: m.id, status: 'empty', updatedAt: null }; }
          }
          if (m.id === 'policy') {
            const docs = await listOrgCollection(orgId, m.file).catch(() => []);
            const ispDoc = docs.find((d) => d.isInformationSecurityPolicy === true);
            if (!ispDoc) return { id: m.id, status: 'empty', updatedAt: null };
            const approved = ispDoc.status === 'Approved' && ispDoc.approvalDate;
            return { id: m.id, status: approved ? 'complete' : 'in-progress', updatedAt: ispDoc?.updatedAt || null };
          }
          if (m.id === 'documents') {
            const docs = await listOrgCollection(orgId, m.file).catch(() => []);
            if (docs.length === 0) return { id: m.id, status: 'empty', updatedAt: null };
            const allApproved = docs.every((d) => d.status === 'Approved');
            return { id: m.id, status: allApproved ? 'complete' : 'in-progress', updatedAt: maxUpdatedAt(docs) };
          }
          if (m.id === 'audits') {
            const items = await listOrgCollection(orgId, m.file).catch(() => []);
            if (items.length === 0) return { id: m.id, status: 'empty', updatedAt: null };
            const allCompleted = items.every((a) => a.status === 'Completed');
            return { id: m.id, status: allCompleted ? 'complete' : 'in-progress', updatedAt: maxUpdatedAt(items) };
          }
          if (m.id === 'objectives') {
            const items = await listOrgCollection(orgId, m.file).catch(() => []);
            if (items.length === 0) return { id: m.id, status: 'empty', updatedAt: null };
            const allCompleted = items.every((o) => o.status === 'Achieved');
            return { id: m.id, status: allCompleted ? 'complete' : 'in-progress', updatedAt: maxUpdatedAt(items) };
          }
          if (m.id === 'management-reviews') {
            const items = await listOrgCollection(orgId, m.file).catch(() => []);
            if (items.length === 0) return { id: m.id, status: 'empty', updatedAt: null };
            const anyPending = items.some((mr) => Array.isArray(mr.actions) && mr.actions.some((a) => a.status !== 'Completed'));
            return { id: m.id, status: anyPending ? 'in-progress' : 'complete', updatedAt: maxUpdatedAt(items) };
          }
          if (m.id === 'nonconformities') {
            const items = await listOrgCollection(orgId, m.file).catch(() => []);
            if (items.length === 0) return { id: m.id, status: 'empty', updatedAt: null };
            const anyOpen = items.some((nc) => nc.status !== 'Closed');
            return { id: m.id, status: anyOpen ? 'in-progress' : 'complete', updatedAt: maxUpdatedAt(items) };
          }
          if (m.id === 'competence') {
            const currentYear = new Date().getFullYear();
            try {
              const prog = await readOrgFile(orgId, `competence/program-${currentYear}.json`);
              if (!prog || !Array.isArray(prog.roles) || prog.roles.length === 0) return { id: m.id, status: 'empty', updatedAt: prog?.updatedAt || null };
              const allActions = prog.roles.flatMap((r) => r.trainingActions || []);
              if (allActions.length > 0 && allActions.every((a) => a.status === 'Completed')) return { id: m.id, status: 'complete', updatedAt: prog?.updatedAt || null };
              return { id: m.id, status: 'in-progress', updatedAt: prog?.updatedAt || null };
            } catch { return { id: m.id, status: 'empty', updatedAt: null }; }
          }
          if (m.id === 'awareness') {
            const currentYear = new Date().getFullYear();
            try {
              const prog = await readOrgFile(orgId, `awareness/program-${currentYear}.json`);
              if (!prog || !Array.isArray(prog.activities) || prog.activities.length === 0) return { id: m.id, status: 'empty', updatedAt: prog?.updatedAt || null };
              if (prog.activities.every((a) => a.status === 'Completed')) return { id: m.id, status: 'complete', updatedAt: prog?.updatedAt || null };
              return { id: m.id, status: 'in-progress', updatedAt: prog?.updatedAt || null };
            } catch { return { id: m.id, status: 'empty', updatedAt: null }; }
          }
          if (m.id === 'metrics') {
            const items = await listOrgCollection(orgId, 'metrics').catch(() => []);
            if (items.length === 0) return { id: m.id, status: 'empty', updatedAt: null };
            const mToday = new Date();
            function mAddMonths(dateStr, n) {
              const d = new Date(dateStr);
              const result = new Date(d.getFullYear(), d.getMonth() + n, d.getDate());
              if (result.getDate() !== d.getDate()) result.setDate(0);
              return result;
            }
            const GRACE = { Weekly: 1, Monthly: 3, Quarterly: 9, Annual: 36 };
            const FREQ_MONTHS = { Weekly: 0, Monthly: 1, Quarterly: 3, Annual: 12 };
            const allMet = items.every(met => {
              const readings = Array.isArray(met.readings) ? met.readings : [];
              if (readings.length === 0) return false;
              const sorted = readings.slice().sort((a, b) => new Date(b.date) - new Date(a.date));
              const latest = sorted[0];
              if (latest.status !== 'Met') return false;
              const grace = GRACE[met.frequency] || 3;
              let expectedNext;
              if (met.frequency === 'Weekly') {
                expectedNext = new Date(new Date(latest.date).getTime() + 7 * MS_PER_DAY);
              } else {
                expectedNext = mAddMonths(latest.date, FREQ_MONTHS[met.frequency] || 1);
              }
              const deadline = new Date(expectedNext.getTime() + grace * MS_PER_DAY);
              return mToday <= deadline;
            });
            return { id: m.id, status: allMet ? 'complete' : 'in-progress', updatedAt: maxUpdatedAt(items) };
          }
          if (m.id === 'legal-register') {
            const items = await listOrgCollection(orgId, m.file).catch(() => []);
            if (items.length === 0) return { id: m.id, status: 'empty', updatedAt: null };
            const allActive = items.every((lr) => lr.status === 'Active');
            return { id: m.id, status: allActive ? 'complete' : 'in-progress', updatedAt: maxUpdatedAt(items) };
          }
          if (m.id === 'risks') {
            const items = await listOrgCollection(orgId, m.file).catch(() => []);
            return { id: m.id, status: items.length > 0 ? 'in-progress' : 'empty', updatedAt: maxUpdatedAt(items) };
          }
          if (m.id === 'assets') {
            const items = await listOrgCollection(orgId, m.file).catch(() => []);
            return { id: m.id, status: items.length > 0 ? 'in-progress' : 'empty', updatedAt: maxUpdatedAt(items) };
          }
          if (m.id === 'suppliers') {
            const items = await listOrgCollection(orgId, m.file).catch(() => []);
            return { id: m.id, status: items.length > 0 ? 'in-progress' : 'empty', updatedAt: maxUpdatedAt(items) };
          }
          if (m.id === 'customers') {
            const items = await listOrgCollection(orgId, m.file).catch(() => []);
            return { id: m.id, status: items.length > 0 ? 'in-progress' : 'empty', updatedAt: maxUpdatedAt(items) };
          }
          if (m.id === 'incidents') {
            const items = await listOrgCollection(orgId, m.file).catch(() => []);
            return { id: m.id, status: items.length > 0 ? 'in-progress' : 'empty', updatedAt: maxUpdatedAt(items) };
          }
          if (m.id === 'soa') {
            try {
              const f = await readOrgFile(orgId, m.file);
              return { id: m.id, status: f ? 'in-progress' : 'empty', updatedAt: f?.updatedAt || null };
            } catch { return { id: m.id, status: 'empty', updatedAt: null }; }
          }
          if (m.id === 'rtp') {
            try {
              const f = await readOrgFile(orgId, m.file);
              return { id: m.id, status: f ? 'in-progress' : 'empty', updatedAt: f?.updatedAt || null };
            } catch { return { id: m.id, status: 'empty', updatedAt: null }; }
          }
          const hasData = m.isCollection
            ? await orgCollectionHasEntries(orgId, m.file)
            : await orgFileExists(orgId, m.file);
          return { id: m.id, status: hasData ? 'in-progress' : 'empty', updatedAt: null };
        })
      );
      if (!cancelled) {
        const statusMap = {};
        const updatedAtMap = {};
        results.forEach(({ id, status, updatedAt }) => {
          statusMap[id] = status;
          if (updatedAt) updatedAtMap[id] = updatedAt;
        });
        setStatuses(statusMap);
        setModuleUpdatedAt(updatedAtMap);
      }
    })();
    return () => { cancelled = true; };
  }, [orgId]);

  // ── Derived values ────────────────────────────────────────────────────────
  const certStatus = org.certificationStatus || 'none';
  const certColors = CERT_STATUS_COLORS[certStatus] || CERT_STATUS_COLORS.none;
  const certLabel = (t.certStatusLabels && t.certStatusLabels[certStatus]) || certStatus;
  const trend = computeTrend(org.healthScoreHistory);
  const nextMilestone = getNextMilestone(org);
  const snoozedEntries = Object.entries(org.alertSnoozes || {}).filter(([, v]) => new Date(v) > new Date());
  const activeAlerts = alerts.filter(a => !snoozedEntries.some(([k]) => k === a.key));
  const snoozedCount = snoozedEntries.length;
  // Bands are derived at render time from the single sorted 60-day list. dlThirty is the
  // near/far boundary, recomputed equivalently here (date ≤ today+30 → near; > today+30 → far),
  // matching the addDl window's inclusive lower bound so day-30 lands in near and day-31 opens far.
  const dlThirty = new Date(new Date().getTime() + 30 * MS_PER_DAY);
  const nearDeadlines = upcomingDeadlines.filter(d => new Date(d.date) <= dlThirty);
  const farDeadlines = upcomingDeadlines.filter(d => new Date(d.date) > dlThirty);
  const visibleNearDeadlines = showAllDeadlines ? nearDeadlines : nearDeadlines.slice(0, 5);
  const visibleFarDeadlines = showAllDeadlines ? farDeadlines : farDeadlines.slice(0, 5);
  // Gap panels: partition the flat gap arrays by gap key (g.gap) into groups, ordered by
  // descending case count (stable on ties: first-appearance order). Presentation only —
  // the gap arrays and the navigation targets are unchanged.
  const groupGapsByType = (gaps) => {
    const order = [];
    const byKey = new Map();
    for (const g of gaps) {
      if (!byKey.has(g.gap)) { byKey.set(g.gap, []); order.push(g.gap); }
      byKey.get(g.gap).push(g);
    }
    return order
      .map((key, idx) => ({ key, cases: byKey.get(key), idx }))
      .sort((a, b) => b.cases.length - a.cases.length || a.idx - b.idx);
  };
  const riskGapGroups = groupGapsByType(riskGaps);
  const documentGapGroups = groupGapsByType(documentGaps);
  const twoCol = winWidth >= 680;
  const daysUntilMilestone = nextMilestone
    ? Math.ceil((new Date(nextMilestone.date) - new Date()) / MS_PER_DAY)
    : null;
  const milestoneDaysColor =
    daysUntilMilestone === null ? TEXT_DISABLED :
    daysUntilMilestone < 0 ? FIELD_ERROR_BORDER : // TODO: P6b — verify color role
    daysUntilMilestone <= 7 ? FIELD_ERROR_BORDER : // TODO: P6b — verify color role
    daysUntilMilestone <= 30 ? STATUS_WARN.text :
    BRAND_PRIMARY;

  // ── Panel shared style ────────────────────────────────────────────────────
  const panel = {
    background: BG_SURFACE, border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 10,
    padding: '1.25rem 1.4rem', boxSizing: 'border-box',
  };
  // Single origin for the panel-title typographic style (family/size/weight via the
  // FONT_TINY spread, plus colour, letterSpacing and uppercase). The bottom margin is
  // NOT embedded here so context owns the vertical spacing: the six action-bar panel
  // titles consume panelTitle directly (their flex header already carries marginBottom
  // 0.9rem), while standalone panel titles use panelLabel = panelTitle + the margin.
  const panelTitle = {
    ...FONT_TINY, color: TEXT_MUTED,
    letterSpacing: '0.05em', textTransform: 'uppercase',
  };
  const panelLabel = {
    ...panelTitle, marginBottom: '0.9rem',
  };
  const linkBtn = {
    ...FONT_TINY,
    background: 'none', border: 'none', cursor: 'pointer',
    color: TEXT_MUTED,
    padding: 0, textDecoration: 'underline', textDecorationColor: BORDER_DEFAULT,
  };

  return (
    <div style={{ minHeight: 'calc(100vh - 52px)', background: BG_CONTENT, padding: '2.5rem 1.5rem', boxSizing: 'border-box' }}>
      <div style={{ maxWidth: CONTENT_MAX_WIDTH, margin: '0 auto' }}>

        {/* ── Org header ─────────────────────────────────────────────────── */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem', marginBottom: '2rem' }}>
          <div>
            <h1 style={{ fontFamily: FONT_FAMILY_DISPLAY, fontWeight: 800, fontSize: '1.75rem', color: TEXT_HEADING, margin: '0 0 0.35rem', lineHeight: 1.15 }}>
              {org.name}
            </h1>
            <p style={{ ...FONT_LABEL, color: TEXT_MUTED, margin: 0, letterSpacing: '0.03em' }}>
              {(SECTORS.find(s => s.value === org.sector)?.[lang === 'es' ? 'nameEs' : 'nameEn']) || org.sector}{(() => {
                const raw = org.country;
                const arr = Array.isArray(raw)
                  ? raw
                  : (typeof raw === 'string' && raw !== '' ? [raw] : []);
                if (arr.length === 0) return '';
                const display = arr
                  .map((code) => (COUNTRIES.find(c => c.code === code)?.[lang === 'es' ? 'nameEs' : 'nameEn']) || code)
                  .join(' · ');
                return ` · ${display}`;
              })()}
            </p>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
            <button onClick={() => onNavigate('executive-dashboard')} style={linkBtn}>{t.executiveDashboard}</button>
            <button onClick={() => setShowDashConfig(v => !v)} style={linkBtn}>{t.configureDashboard}</button>
            <button onClick={() => onNavigate('settings')} style={linkBtn}>{t.settings}</button>
            <button onClick={onSwitchOrg} style={linkBtn}>{t.switchOrg}</button>
          </div>
        </div>

        {/* ── Dashboard configuration panel ────────────────────────────────── */}
        {showDashConfig && (
          <div style={{ ...panel, marginBottom: '1.25rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.9rem' }}>
              <span style={panelTitle}>{t.configureTitle}</span>
              <button onClick={() => setShowDashConfig(false)} style={linkBtn}>{t.closeConfig}</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: twoCol ? 'repeat(2, 1fr)' : '1fr', gap: '0.5rem' }}>
              {Object.keys(DEFAULT_DASH_CONFIG).map(panelId => (
                <label key={panelId} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={!!dashConfig[panelId]}
                    onChange={() => togglePanel(panelId)}
                    style={{ accentColor: BRAND_PRIMARY }}
                  />
                  <span style={{ ...FONT_LABEL, color: TEXT_DEFAULT }}>
                    {(t.panelLabels && t.panelLabels[panelId]) || panelId}
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* ── Compact top strip: cert status + next milestone + countdown ─── */}
        <div style={{
          ...panel,
          padding: 0,
          marginBottom: '1.25rem',
          display: 'flex',
          flexDirection: twoCol ? 'row' : 'column',
          overflow: 'hidden',
        }}>
          {/* Certification status */}
          <div style={{
            flex: 1,
            padding: '0.85rem 1.25rem',
            borderRight: twoCol && nextMilestone ? `1px solid ${BORDER_DEFAULT}` : 'none',
            borderBottom: !twoCol && nextMilestone ? `1px solid ${BORDER_DEFAULT}` : 'none',
            display: 'flex', flexDirection: 'column', justifyContent: 'center',
          }}>
            <div style={{ ...panelLabel, marginBottom: '0.35rem' }}>{t.certStatusLabel}</div>
            <span style={{
              ...FONT_BUTTON,
              color: certColors.text, background: certColors.bg,
              border: `1px solid ${certColors.border}`, borderRadius: 6,
              padding: '0.15rem 0.55rem', alignSelf: 'flex-start',
            }}>
              {certLabel}
            </span>
          </div>
          {nextMilestone && (
            <>
              {/* Next milestone */}
              <div style={{
                flex: 1,
                padding: '0.85rem 1.25rem',
                borderRight: twoCol ? `1px solid ${BORDER_DEFAULT}` : 'none',
                borderBottom: !twoCol ? `1px solid ${BORDER_DEFAULT}` : 'none',
                display: 'flex', flexDirection: 'column', justifyContent: 'center',
              }}>
                <div style={{ ...panelLabel, marginBottom: '0.35rem' }}>{t.nextMilestone}</div>
                <span style={{ ...FONT_BUTTON, color: TEXT_HEADING }}>
                  {nextMilestone.label[lang]} — {fmtDate(nextMilestone.date, lang)}
                </span>
              </div>
              {/* Days remaining */}
              <div style={{
                flex: 1,
                padding: '0.85rem 1.25rem',
                display: 'flex', flexDirection: 'column', justifyContent: 'center',
              }}>
                <div style={{ ...panelLabel, marginBottom: '0.35rem' }}>{t.daysRemaining}</div>
                <span style={{
                  ...FONT_BUTTON,
                  color: milestoneDaysColor,
                }}>
                  {daysUntilMilestone} d
                </span>
              </div>
            </>
          )}
        </div>

        {/* ── Row 1: Health Score + Risk Posture + Nonconformities ────────── */}
        {(() => {
          const visibleKpiCount = ['healthScore', 'riskPosture', 'nonconformities'].filter(k => dashConfig[k]).length;
          if (visibleKpiCount === 0) return null;
          return (
        <div style={{ display: 'grid', gridTemplateColumns: twoCol ? `repeat(${visibleKpiCount}, 1fr)` : '1fr', gap: '1.25rem', marginBottom: '1.25rem' }}>

          {dashConfig.healthScore && (
          /* Health Score */
          <div style={panel}>
            <div style={panelLabel}>{t.healthScoreTitle}</div>
            {intelligenceLoading ? <SkeletonCard height={100} /> : (
              <>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <span style={{ fontFamily: FONT_FAMILY_DISPLAY, fontWeight: 800, fontSize: '2.8rem', color: healthScore !== null ? scoreColor(healthScore) : TEXT_DISABLED, lineHeight: 1 }}>
                    {healthScore !== null ? healthScore : '—'}
                  </span>
                  <span style={{ ...FONT_SMALL, color: TEXT_DISABLED }}>/100</span>
                  {trend !== null && (
                    <span style={{ ...FONT_SMALL, color: trend > 0 ? BRAND_PRIMARY : trend < 0 ? FIELD_ERROR_BORDER /* TODO: P6b — verify color role */ : TEXT_DISABLED }}>
                      {trend > 0 ? `↑ +${trend}` : trend < 0 ? `↓ ${trend}` : '→ 0'}
                    </span>
                  )}
                </div>
                {healthScore !== null && (
                  <div style={{ height: 6, background: '#e5e3df', borderRadius: 3, marginBottom: '0.75rem', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${healthScore}%`, background: scoreColor(healthScore), borderRadius: 3, transition: 'width 0.4s ease' }} />
                  </div>
                )}
                {hardGateFired && (
                  <div style={{ ...FONT_TINY, color: STATUS_WARN.text, background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 5, padding: '0.3rem 0.55rem', marginBottom: '0.65rem' }}>
                    {t.hardGateNote}
                  </div>
                )}
                <button onClick={() => setShowScoreBreakdown(v => !v)} style={linkBtn}>
                  {showScoreBreakdown ? t.hideBreakdown : t.viewBreakdown}
                </button>
                {showScoreBreakdown && scoreComponents && (
                  <div style={{ marginTop: '0.85rem', display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
                    {Object.entries(HEALTH_COMPONENT_WEIGHTS).map(([key, weight]) => {
                      const val = scoreComponents[key] != null ? scoreComponents[key] : 0;
                      const pct = Math.round((val / weight) * 100);
                      const barColor = pct >= 70 ? BRAND_PRIMARY : pct >= 40 ? '#f59e0b' : FIELD_ERROR_BORDER; // TODO: P6b — verify color role
                      return (
                        <div key={key}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.12rem' }}>
                            <span style={{ ...FONT_TINY, color: TEXT_MUTED }}>
                              {(t.componentLabels && t.componentLabels[key]) || key}
                            </span>
                            <span style={{ ...FONT_TINY, color: TEXT_DISABLED }}>
                              {Math.round(val * 10) / 10}/{weight}
                            </span>
                          </div>
                          <div style={{ height: 4, background: '#e5e3df', borderRadius: 2, overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${pct}%`, background: barColor, borderRadius: 2 }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
          )}

          {dashConfig.riskPosture && (
          /* Risk posture */
          <div style={{ ...panel, cursor: riskCounts && riskCounts.total > 0 ? 'pointer' : 'default' }} onClick={() => riskCounts && riskCounts.total > 0 && onNavigate('risks')}>
            <div style={panelLabel}>{t.riskPosture}</div>
            {intelligenceLoading ? <SkeletonCard height={70} /> : !riskCounts || riskCounts.total === 0 ? (
              <p style={{ ...FONT_SMALL, color: TEXT_DISABLED, margin: 0 }}>{t.noRisks}</p>
            ) : (
              <>
                <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
                  {[
                    { label: t.high, count: riskCounts.high, color: FIELD_ERROR_BORDER }, // TODO: P6b — verify color role
                    { label: t.medium, count: riskCounts.medium, color: '#d97706' },
                    { label: t.low, count: riskCounts.low, color: BRAND_PRIMARY },
                  ].map(({ label, count, color }) => (
                    <div key={label} style={{ textAlign: 'center' }}>
                      <div style={{ fontFamily: FONT_FAMILY_DISPLAY, fontWeight: 800, fontSize: '1.8rem', color, lineHeight: 1 }}>{count}</div>
                      <div style={{ ...FONT_TINY, color: TEXT_MUTED }}>{label}</div>
                    </div>
                  ))}
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontFamily: FONT_FAMILY_DISPLAY, fontWeight: 800, fontSize: '1.8rem', color: TEXT_DISABLED, lineHeight: 1 }}>{riskCounts.total}</div>
                    <div style={{ ...FONT_TINY, color: TEXT_MUTED }}>total</div>
                  </div>
                </div>
                <span style={{ ...FONT_TINY, color: TEXT_MUTED }}>
                  {riskCounts.treated} {t.treated} · {t.viewRisks}
                </span>
              </>
            )}
          </div>
          )}

          {dashConfig.nonconformities && (
          /* Open NCs */
          <div style={{ ...panel, cursor: ncCounts && ncCounts.open > 0 ? 'pointer' : 'default' }} onClick={() => ncCounts && ncCounts.open > 0 && onNavigate('nonconformities')}>
            <div style={panelLabel}>{t.openNcsTitle}</div>
            {intelligenceLoading ? <SkeletonCard height={70} /> : !ncCounts || ncCounts.open === 0 ? (
              <p style={{ ...FONT_SMALL, color: TEXT_DISABLED, margin: 0 }}>{t.noNcs}</p>
            ) : (
              <>
                <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontFamily: FONT_FAMILY_DISPLAY, fontWeight: 800, fontSize: '1.8rem', color: TEXT_HEADING, lineHeight: 1 }}>{ncCounts.open}</div>
                    <div style={{ ...FONT_TINY, color: TEXT_MUTED }}>{t.openLabel}</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontFamily: FONT_FAMILY_DISPLAY, fontWeight: 800, fontSize: '1.8rem', color: TEXT_DISABLED, lineHeight: 1 }}>{ncCounts.closed}</div>
                    <div style={{ ...FONT_TINY, color: TEXT_MUTED }}>{t.closedLabel}</div>
                  </div>
                  {ncCounts.overdueActions > 0 && (
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontFamily: FONT_FAMILY_DISPLAY, fontWeight: 800, fontSize: '1.8rem', color: FIELD_ERROR_BORDER /* TODO: P6b — verify color role */, lineHeight: 1 }}>{ncCounts.overdueActions}</div>
                      <div style={{ ...FONT_TINY, color: FIELD_ERROR_BORDER /* TODO: P6b — verify color role */ }}>{t.overdueActionsLabel}</div>
                    </div>
                  )}
                </div>
                <span style={{ ...FONT_TINY, color: TEXT_MUTED }}>
                  {t.viewNcs}
                </span>
              </>
            )}
          </div>
          )}
        </div>
          );
        })()}

        {/* ── Alerts (full width, collapsed by default) ───────────────────── */}
        {dashConfig.alerts && (
        <div style={{ ...panel, marginBottom: '1.25rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.9rem', flexWrap: 'wrap', gap: '0.4rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
              <span style={panelTitle}>
                {t.alertsTitle}
              </span>
              <button onClick={() => setAlertsExpanded(v => !v)} style={linkBtn}>
                {alertsExpanded ? t.showLess : `${t.showAll} (${activeAlerts.length})`}
              </button>
            </div>
            <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
              {SEVERITY_ORDER.map(sev => {
                const cnt = alerts.filter(a => a.severity === sev).length;
                if (cnt === 0) return null;
                return (
                  <span key={sev} style={{ ...FONT_TINY, background: SEV_COLORS[sev].bg, color: SEV_COLORS[sev].text, border: `1px solid ${SEV_COLORS[sev].border}`, borderRadius: 10, padding: '0.1rem 0.45rem' }}>
                    {cnt} {t.severityLabels && t.severityLabels[sev]}
                  </span>
                );
              })}
            </div>
          </div>

          {/* Summary line — always visible */}
          <div style={{ ...FONT_SMALL, color: TEXT_MUTED }}>
            {activeAlerts.length} {t.alertsNone.replace(/^(No|Sin)\s+/i, '')} · {snoozedCount} {t.showSnoozed.replace(/^(Show|Mostrar)\s+/i, '')}
          </div>

          {/* Expanded content: full list + snoozed filter */}
          {alertsExpanded && (
            <>
              <div style={{ marginTop: '0.85rem' }}>
                {intelligenceLoading ? <SkeletonCard height={100} /> : alerts.length === 0 ? (
                  <p style={{ ...FONT_SMALL, color: TEXT_DISABLED, margin: 0 }}>
                    {t.alertsNone}
                  </p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                    {SEVERITY_ORDER.map(sev => {
                      const sevAlerts = alerts.filter(a => a.severity === sev);
                      if (sevAlerts.length === 0) return null;
                      return sevAlerts.map(a => (
                        <AlertRow
                          key={a.key}
                          alert={a}
                          t={t}
                          lang={lang}
                          onNavigate={onNavigate}
                          isOpen={openAlertGroups.has(a.key)}
                          onToggle={() => toggleGapGroup(setOpenAlertGroups, a.key)}
                          onSnooze={handleSnoozeAlert}
                          caseSnoozes={org.alertCaseSnoozes || {}}
                          onSnoozeCase={handleSnoozeCase}
                          onClearSnoozeCase={handleClearSnoozeCase}
                        />
                      ));
                    })}
                  </div>
                )}
              </div>
              {snoozedEntries.length > 0 && (
                <div style={{ marginTop: '0.85rem', borderTop: '1px solid #e5e3df', paddingTop: '0.75rem' }}>
                  <button onClick={() => setShowSnoozed(v => !v)} style={linkBtn}>
                    {showSnoozed ? t.hideSnoozed : `${t.showSnoozed} (${snoozedEntries.length})`}
                  </button>
                  {showSnoozed && (
                    <div style={{ marginTop: '0.6rem', display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                      {snoozedEntries.map(([key, until]) => (
                        <div key={key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', padding: '0.3rem 0.5rem' }}>
                          <span style={{ ...FONT_TINY, color: TEXT_DISABLED, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {(t.alertTitles && t.alertTitles[key]) || key}
                          </span>
                          <span style={{ ...FONT_TINY, color: TEXT_DISABLED, flexShrink: 0 }}>
                            {t.snoozedUntil} {fmtDate(until, lang)}
                          </span>
                          <button onClick={() => handleClearSnooze(key)} style={linkBtn}>{t.reactivate}</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
        )}

        {/* ── Risk gaps panel (§7 Gap detection — 6 Risk predicates) ───────── */}
        {dashConfig.riskGaps && (
        <div style={{ ...panel, marginBottom: '1.25rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.9rem', flexWrap: 'wrap', gap: '0.4rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
              <span style={panelTitle}>
                {t.riskGapsTitle}
              </span>
              {riskGaps.length > 0 && (
                <button
                  onClick={() => setOpenRiskGapGroups(prev =>
                    prev.size > 0 ? new Set() : new Set(riskGapGroups.map(grp => grp.key)))}
                  style={linkBtn}
                >
                  {openRiskGapGroups.size > 0 ? t.showLess : `${t.showAll} (${riskGaps.length})`}
                </button>
              )}
            </div>
          </div>

          <div style={{ ...FONT_SMALL, color: TEXT_MUTED }}>
            {riskGaps.length === 0
              ? t.riskGapsNone
              : `${riskGaps.length} ${t.riskGapsTitle.toLowerCase()}`}
          </div>

          {riskGaps.length > 0 && (
            <div style={{ marginTop: '0.85rem', display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
              {riskGapGroups.map(group => {
                const groupLabel = (t.riskGapLabels && t.riskGapLabels[group.key]) || group.key;
                const open = openRiskGapGroups.has(group.key);
                return (
                  <div key={group.key} style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                    <button type="button"
                      onClick={() => toggleGapGroup(setOpenRiskGapGroups, group.key)}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        gap: '0.6rem', padding: '0.45rem 0.65rem',
                        background: BG_TABLE, border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 7,
                        width: '100%', textAlign: 'left', cursor: 'pointer',
                        font: 'inherit', color: 'inherit',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1, minWidth: 0 }}>
                        <span style={{ ...FONT_TINY, color: TEXT_MUTED, flexShrink: 0 }}>{open ? '▾' : '▸'}</span>
                        <span style={{
                          ...FONT_SMALL, color: TEXT_DEFAULT,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                          {groupLabel}
                        </span>
                      </div>
                      <span style={{ ...FONT_TINY, color: TEXT_MUTED, flexShrink: 0 }}>({group.cases.length})</span>
                    </button>
                    {open && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', paddingLeft: '1.25rem' }}>
                        {group.cases.map((g, i) => {
                          const title = (typeof g.riskSummary === 'string' && g.riskSummary.trim() !== '')
                            ? g.riskSummary
                            : (t.deadlineFallbacks?.risk || '');
                          return (
                            <button type="button"
                              key={`${g.riskId}-${g.gap}-${i}`}
                              onClick={() => onNavigate('risks', { editId: g.riskId })}
                              onMouseEnter={e => { e.currentTarget.style.background = BG_HOVER_LT; }}
                              onMouseLeave={e => { e.currentTarget.style.background = BG_TABLE; }}
                              onFocus={e => { e.currentTarget.style.outline = `2px solid ${BRAND_PRIMARY}`; e.currentTarget.style.outlineOffset = '2px'; }}
                              onBlur={e => { e.currentTarget.style.outline = 'none'; e.currentTarget.style.outlineOffset = '0'; }}
                              style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                gap: '0.6rem', padding: '0.45rem 0.65rem',
                                background: BG_TABLE, border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 7,
                                width: '100%', textAlign: 'left', cursor: 'pointer',
                                font: 'inherit', color: 'inherit',
                              }}
                            >
                              <span style={{
                                ...FONT_SMALL, color: TEXT_DEFAULT,
                                flex: 1, minWidth: 0,
                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                              }}>
                                {`${g.riskRef} - ${title}`}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        )}

        {/* ── Document gaps panel (§7 Gap detection — Document predicates) ─── */}
        {dashConfig.documentGaps && (
        <div style={{ ...panel, marginBottom: '1.25rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.9rem', flexWrap: 'wrap', gap: '0.4rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
              <span style={panelTitle}>
                {t.documentGapsTitle}
              </span>
              {documentGaps.length > 0 && (
                <button
                  onClick={() => setOpenDocumentGapGroups(prev =>
                    prev.size > 0 ? new Set() : new Set(documentGapGroups.map(grp => grp.key)))}
                  style={linkBtn}
                >
                  {openDocumentGapGroups.size > 0 ? t.showLess : `${t.showAll} (${documentGaps.length})`}
                </button>
              )}
            </div>
          </div>

          <div style={{ ...FONT_SMALL, color: TEXT_MUTED }}>
            {documentGaps.length === 0
              ? t.documentGapsNone
              : `${documentGaps.length} ${t.documentGapsTitle.toLowerCase()}`}
          </div>

          {documentGaps.length > 0 && (
            <div style={{ marginTop: '0.85rem', display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
              {documentGapGroups.map(group => {
                const groupLabel = (t.documentGapLabels && t.documentGapLabels[group.key]) || group.key;
                const open = openDocumentGapGroups.has(group.key);
                return (
                  <div key={group.key} style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                    <button type="button"
                      onClick={() => toggleGapGroup(setOpenDocumentGapGroups, group.key)}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        gap: '0.6rem', padding: '0.45rem 0.65rem',
                        background: BG_TABLE, border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 7,
                        width: '100%', textAlign: 'left', cursor: 'pointer',
                        font: 'inherit', color: 'inherit',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1, minWidth: 0 }}>
                        <span style={{ ...FONT_TINY, color: TEXT_MUTED, flexShrink: 0 }}>{open ? '▾' : '▸'}</span>
                        <span style={{
                          ...FONT_SMALL, color: TEXT_DEFAULT,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                          {groupLabel}
                        </span>
                      </div>
                      <span style={{ ...FONT_TINY, color: TEXT_MUTED, flexShrink: 0 }}>({group.cases.length})</span>
                    </button>
                    {open && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', paddingLeft: '1.25rem' }}>
                        {group.cases.map((g, i) => {
                          const title = (typeof g.documentTitle === 'string' && g.documentTitle.trim() !== '')
                            ? g.documentTitle
                            : (t.deadlineFallbacks?.document || '');
                          return (
                            <button type="button"
                              key={`${g.documentId}-${g.gap}-${i}`}
                              onClick={() => onNavigate('documents', { editId: g.documentId })}
                              onMouseEnter={e => { e.currentTarget.style.background = BG_HOVER_LT; }}
                              onMouseLeave={e => { e.currentTarget.style.background = BG_TABLE; }}
                              onFocus={e => { e.currentTarget.style.outline = `2px solid ${BRAND_PRIMARY}`; e.currentTarget.style.outlineOffset = '2px'; }}
                              onBlur={e => { e.currentTarget.style.outline = 'none'; e.currentTarget.style.outlineOffset = '0'; }}
                              style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                gap: '0.6rem', padding: '0.45rem 0.65rem',
                                background: BG_TABLE, border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 7,
                                width: '100%', textAlign: 'left', cursor: 'pointer',
                                font: 'inherit', color: 'inherit',
                              }}
                            >
                              <span style={{
                                ...FONT_SMALL, color: TEXT_DEFAULT,
                                flex: 1, minWidth: 0,
                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                              }}>
                                {title}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        )}

        {/* ── Orphan references panel (§7 Orphan reference detection) ─────── */}
        {dashConfig.orphanRefs && (
        <div style={{ ...panel, marginBottom: '1.25rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.9rem', flexWrap: 'wrap', gap: '0.4rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
              <span style={panelTitle}>
                {t.orphanRefsTitle}
              </span>
              {orphans.length > 0 && (
                <button onClick={() => setOrphansExpanded(v => !v)} style={linkBtn}>
                  {orphansExpanded ? t.showLess : `${t.showAll} (${orphans.length})`}
                </button>
              )}
            </div>
          </div>

          <div style={{ ...FONT_SMALL, color: TEXT_MUTED }}>
            {orphans.length === 0
              ? t.orphanRefsNone
              : t.orphanRefsCount.replace('{count}', orphans.length)}
          </div>

          {orphansExpanded && orphans.length > 0 && (
            <div style={{ marginTop: '0.85rem', display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
              {orphans.map((o) => {
                const typeLabel = (t.holderTypeLabels && t.holderTypeLabels[o.holderType]) || o.holderType;
                return (
                  <div key={`${o.holderType}-${o.holderId}-${o.field}`} style={{
                    display: 'flex', alignItems: 'center', gap: '0.6rem',
                    padding: '0.45rem 0.65rem',
                    background: BG_TABLE, border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 7,
                  }}>
                    <span style={{
                      ...FONT_TINY, color: TEXT_DISABLED, flexShrink: 0,
                      textTransform: 'uppercase', letterSpacing: '0.04em',
                    }}>
                      {typeLabel}
                    </span>
                    <span style={{ ...FONT_SMALL, color: TEXT_DEFAULT, flexShrink: 0 }}>
                      {o.holderRef}
                    </span>
                    <span style={{ ...FONT_TINY, color: TEXT_MUTED, flexShrink: 0 }}>
                      {o.field}
                    </span>
                    <span style={{
                      ...FONT_TINY, color: TEXT_DISABLED,
                      flex: 1, minWidth: 0,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {o.orphanIds.join(', ')}
                    </span>
                    <button
                      onClick={() => onNavigate(HOLDER_TYPE_TO_MODULE[o.holderType], { editId: o.holderId })}
                      style={{
                        ...FONT_TINY,
                        background: 'none', border: 'none', cursor: 'pointer',
                        color: TEXT_DEFAULT, padding: 0, flexShrink: 0, opacity: 0.7,
                      }}
                    >
                      {t.openHolder}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        )}

        {/* ── Panel 4: Upcoming deadlines ──────────────────────────────────── */}
        {dashConfig.deadlines && (
        <div style={{ ...panel, marginBottom: '1.25rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.9rem' }}>
            <span style={panelTitle}>
              {t.deadlinesTitle}
            </span>
            {upcomingDeadlines.length > 5 && (
              <button onClick={() => setShowAllDeadlines(v => !v)} style={linkBtn}>
                {showAllDeadlines ? t.showLess : `${t.showAll} (${upcomingDeadlines.length})`}
              </button>
            )}
          </div>
          {intelligenceLoading ? <SkeletonCard height={60} /> : upcomingDeadlines.length === 0 ? (
            <p style={{ ...FONT_SMALL, color: TEXT_DISABLED, margin: 0 }}>{t.noDeadlines}</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.9rem' }}>
              {visibleNearDeadlines.length > 0 && (
                <div>
                  <div style={{ ...FONT_TINY, color: TEXT_MUTED, marginBottom: '0.5rem' }}>{t.deadlinesBandNear}</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                    {visibleNearDeadlines.map((item, i) => (
                      <DeadlineRow key={i} item={item} lang={lang} t={t} onNavigate={onNavigate} />
                    ))}
                  </div>
                </div>
              )}
              {visibleFarDeadlines.length > 0 && (
                <div>
                  <div style={{ ...FONT_TINY, color: TEXT_MUTED, marginBottom: '0.5rem' }}>{t.deadlinesBandFar}</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                    {visibleFarDeadlines.map((item, i) => (
                      <DeadlineRow key={i} item={item} lang={lang} t={t} onNavigate={onNavigate} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        )}

        {/* ── Panel 5: Control implementation gap ─────────────────────────── */}
        {dashConfig.controlGap && (
        <div style={{ ...panel, marginBottom: '1.25rem' }}>
          <div style={panelLabel}>{t.controlGapTitle}</div>
          {intelligenceLoading ? <SkeletonCard height={50} /> : !controlGap || controlGap.total === 0 ? (
            <p style={{ ...FONT_SMALL, color: TEXT_DISABLED, margin: 0 }}>{t.noControls}</p>
          ) : (
            <>
              <div style={{ height: 14, display: 'flex', borderRadius: 4, overflow: 'hidden', marginBottom: '0.65rem', gap: 1 }}>
                {[
                  { count: controlGap.implemented, color: BRAND_PRIMARY },
                  { count: controlGap.partial, color: '#d97706' },
                  { count: controlGap.notImplemented, color: FIELD_ERROR_BORDER }, // TODO: P6b — verify color role
                  { count: Math.max(0, controlGap.total - controlGap.implemented - controlGap.partial - controlGap.notImplemented), color: '#e5e3df' },
                ].filter(s => s.count > 0).map((s, i) => (
                  <div key={i} style={{ flex: s.count, background: s.color }} />
                ))}
              </div>
              <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap', alignItems: 'center' }}>
                {[
                  { label: t.implLabel, count: controlGap.implemented, color: BRAND_PRIMARY },
                  { label: t.partialLabel, count: controlGap.partial, color: '#d97706' },
                  { label: t.notImplLabel, count: controlGap.notImplemented, color: FIELD_ERROR_BORDER }, // TODO: P6b — verify color role
                ].map(({ label, count, color }) => (
                  <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, display: 'inline-block', flexShrink: 0 }} />
                    <span style={{ ...FONT_SMALL, color: TEXT_HEADING }}>{count}</span>
                    <span style={{ ...FONT_SMALL, color: TEXT_MUTED }}>{label}</span>
                  </div>
                ))}
                <span style={{ ...FONT_TINY, color: TEXT_DISABLED, marginLeft: 'auto' }}>
                  {controlGap.total} {t.applicableLabel}
                </span>
              </div>
              <button onClick={() => onNavigate('soa')} style={{ ...linkBtn, marginTop: '0.7rem', display: 'block' }}>
                {t.viewSoa}
              </button>
            </>
          )}
        </div>
        )}

        {/* ── Panel 6: Annex A compliance bars ────────────────────────────── */}
        {dashConfig.annexA && (
        <div style={{ ...panel, marginBottom: '1.25rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.9rem' }}>
            <span style={panelTitle}>
              {t.annexATitle}
            </span>
            <button onClick={() => onNavigate('soa')} style={linkBtn}>{t.viewSoa}</button>
          </div>
          {intelligenceLoading ? <SkeletonCard height={80} /> : !annexAGap || Object.values(annexAGap).every(s => s.applicable === 0) ? (
            <p style={{ ...FONT_SMALL, color: TEXT_DISABLED, margin: 0 }}>{t.noControls}</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
              {['a5', 'a6', 'a7', 'a8'].map(key => {
                const sec = annexAGap[key] || { applicable: 0, implemented: 0, partial: 0 };
                const notImpl = Math.max(0, sec.applicable - sec.implemented - sec.partial);
                const pct = sec.applicable === 0 ? 0 : Math.round((sec.implemented + sec.partial * 0.5) / sec.applicable * 100);
                return (
                  <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <span style={{ ...FONT_TINY, color: TEXT_MUTED, width: 180, flexShrink: 0 }}>
                      {t.annexA && t.annexA[key]}
                    </span>
                    <div style={{ flex: 1, height: 14, display: 'flex', borderRadius: 4, overflow: 'hidden', gap: 1 }}>
                      {sec.applicable === 0 ? (
                        <div style={{ flex: 1, background: '#e5e3df' }} />
                      ) : (
                        <>
                          {sec.implemented > 0 && <div style={{ flex: sec.implemented, background: BRAND_PRIMARY }} />}
                          {sec.partial > 0 && <div style={{ flex: sec.partial, background: '#d97706' }} />}
                          {notImpl > 0 && <div style={{ flex: notImpl, background: '#e5e3df' }} />}
                        </>
                      )}
                    </div>
                    <span style={{ ...FONT_SMALL, color: TEXT_MUTED, width: '2.5rem', textAlign: 'right', flexShrink: 0 }}>
                      {sec.applicable === 0 ? 'N/A' : `${pct}%`}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        )}

        {/* ── Compliance Score panel ───────────────────────────────────────── */}
        {dashConfig.complianceScore && (
        <div style={{ ...panel, marginBottom: '1.25rem' }}>
          <div style={panelLabel}>{t.complianceTitle}</div>
          {complianceResult === null ? <SkeletonCard height={100} /> : (
            <>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <span style={{ fontFamily: FONT_FAMILY_DISPLAY, fontWeight: 800, fontSize: '2.8rem', color: scoreColor(complianceResult.score), lineHeight: 1 }}>
                  {complianceResult.score}
                </span>
                <span style={{ ...FONT_SMALL, color: TEXT_DISABLED }}>/100</span>
              </div>
              <div style={{ height: 6, background: '#e5e3df', borderRadius: 3, marginBottom: '0.75rem', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${complianceResult.score}%`, background: scoreColor(complianceResult.score), borderRadius: 3, transition: 'width 0.4s ease' }} />
              </div>
              {complianceResult.hardGateFired && (
                <div style={{ ...FONT_TINY, color: STATUS_WARN.text, background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 5, padding: '0.3rem 0.55rem', marginBottom: '0.65rem' }}>
                  {t.complianceHardGateNote}
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: twoCol ? 'repeat(2, 1fr)' : '1fr', gap: '0.35rem 1rem', marginTop: '0.75rem' }}>
                {['cl4', 'cl5', 'cl6', 'cl7', 'cl8', 'cl9', 'cl10'].map(key => {
                  const pct = Math.round(complianceResult.clauseScores[key] || 0);
                  return (
                    <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ ...FONT_TINY, color: TEXT_MUTED, minWidth: 140, flexShrink: 0 }}>
                        {(t.clauseLabels && t.clauseLabels[key]) || key}
                      </span>
                      <div style={{ flex: 1, height: 6, background: '#e5e3df', borderRadius: 3, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${pct}%`, background: BRAND_PRIMARY, borderRadius: 3 }} />
                      </div>
                      <span style={{ ...FONT_TINY, color: TEXT_MUTED, width: 32, textAlign: 'right', flexShrink: 0 }}>
                        {pct}%
                      </span>
                    </div>
                  );
                })}
              </div>
              <div style={{ ...FONT_TINY, color: TEXT_DISABLED, marginTop: '0.65rem' }}>
                {t.trackedCoverageNote.replace('{pct}', complianceResult.trackedCoverage)}
              </div>
            </>
          )}
        </div>
        )}

        {/* ── Panel 7: ISMS progress ───────────────────────────────────────── */}
        {dashConfig.ismsProgress && (
        <div style={{ ...panel, marginBottom: '2rem' }}>
          <div style={panelLabel}>{t.modulesTitle}</div>
          <div>
            {CLAUSE_GROUPS.map((group, groupIndex) => {
              const groupModules = group.moduleIds
                .map(id => MODULES.find(m => m.id === id))
                .filter(Boolean);
              return (
                <div key={group.key}>
                  <div style={{
                    ...FONT_TINY, color: TEXT_DISABLED,
                    textTransform: 'uppercase', letterSpacing: '0.06em',
                    paddingTop: '0.6rem', paddingBottom: '0.15rem',
                    borderTop: groupIndex > 0 ? `1px solid ${BORDER_DEFAULT}` : 'none',
                  }}>
                    {group.label[lang] || group.label.en}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: twoCol ? '1fr 1fr' : '1fr', gap: '0' }}>
                  {groupModules.map((m) => {
                    const statusKey = statuses[m.id] || 'empty';
                    const colors = STATUS_COLORS[statusKey] || STATUS_COLORS.empty;
                    const statusLabel = {
                      empty: t.statusEmpty,
                      'in-progress': t.statusInProgress,
                      complete: t.statusComplete,
                    }[statusKey] || t.statusEmpty;
                    return (
                      <div key={m.id} style={{ padding: '0.3rem 0.5rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                          <span style={{ width: 7, height: 7, borderRadius: '50%', background: colors.dot, flexShrink: 0 }} />
                          <span style={{ ...FONT_LIST_ITEM, color: TEXT_HEADING, flex: 1 }}>
                            {m.label[lang] || m.label.en}
                          </span>
                          <span style={{ ...FONT_TINY, color: colors.text }}>
                            {statusLabel}
                          </span>
                        </div>
                        {moduleUpdatedAt[m.id] && (
                          <div style={{ ...FONT_TINY, color: TEXT_DISABLED, paddingLeft: '1.1rem' }}>
                            {fmtDate(moduleUpdatedAt[m.id], lang)}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        )}

      </div>
    </div>
  );
}
